/* eslint-disable react/no-unescaped-entities, react/no-unstable-nested-components */
import { requestCallConfirmation } from '../components/ConfirmCallDialog';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { userCanUseBooking } from '../lib/bookingScope';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import { confirmFinancialChanges } from '../lib/financialConfirm';
import TransactionalSmsModal from '../components/TransactionalSmsModal';
import ClientLiveActivityStrip from '../components/ClientLiveActivityStrip';  // v3.18-1337 LAC-001
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ClientCommunications from '../components/ClientCommunications';
import ClientCommunicationsTab from '../components/ClientCommunicationsTab';  // v3.18-1296
import UnifiedTimeline from '../components/UnifiedTimeline';  // v3.18-1300
import LastCommunicationStrip from '../components/LastCommunicationStrip';  // v3.21-1813 Smart Client Communication Awareness
import ClientGmailThreadPanel from '../components/ClientGmailThreadPanel';  // v3.18-1385
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { canUsePhoneSystem } from '../lib/phone_access';
import { getAttentionStyle, normalizeAttentionColor } from '../lib/noteAttention';  // v3.21-1667
import NoteAttentionPicker from '../components/NoteAttentionPicker';  // v3.21-1667
// v3.18-1260 PAYMENT-005 — Receipt viewer on payment_* activities
import ReceiptViewer from '../components/ReceiptViewer';
import { Receipt as ReceiptIcon } from 'lucide-react';
import ScheduleAppointmentModal from '../components/ScheduleAppointmentModal';
// v3.14-301: CreditApplicationDetails import removed — financing fields now in Client Info
import PTCDealFinancials from '../components/PTCDealFinancials';
import DealFinancialPanel from '../components/DealFinancialPanel';  // v3.14-100: Condensed financial panel
// v3.21-1742: DealCompensationVisibility component import removed —
// no active rendering on Deal page.  Component file kept dormant.
import TimeshareOwnershipTab from '../components/TimeshareOwnershipTab';  // v3.15-1224 OWNERSHIP-001
import PaymentMethodsTab from '../components/PaymentMethodsTab';  // v3.15-1230 SMART-INTAKE-003
// v3.15-1235 — PriorityQueueClientPanel removed with the standalone Priority Queue module.
import { PhoneClickable } from '../components/PhoneQRModal';  // v3.14-109: Scan-to-Call QR
// v3.21-1612 · CallOutcomeModal retired — ActiveCallPill is SSOT for
// disposition + notes + End Call.
// v3.15-976: DealOutcomeGate / DealOutcomeBanner removed — email-only follow-up.
import BillSyncBadge from '../components/BillSyncBadge';  // v3.15-977
import PaidInFullConfirmModal from '../components/PaidInFullConfirmModal';  // v3.15-981
// v3.14-175: DealStageHeader removed — Stage shown in DealDashboard compact strip
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  User,
  Calendar,
  Edit,
  Trash2,
  Plus,
  FileText,
  Upload,
  Download,
  Loader2,
  CheckCircle,
  Clock,
  TrendingUp,
  ClipboardList,
  UserPlus,
  DollarSign,
  Lock,
  Landmark,
  Calculator,
  Users,
  Briefcase,
  Save,
  CreditCard,
  Home,
  Banknote,
  Eye,
  EyeOff,
  Check,
  X,
  Circle,
  ArrowRight,
  LayoutGrid,
  List,
  ChevronUp,
  ChevronDown,
  Merge,
  Search,
  Receipt,
  PhoneOutgoing,
  Shield,
  Package,
  Truck,
  MapPin,
  Copy,
  Link as LinkIcon,
  CalendarPlus,
  UserCheck,
  MessageSquare,
  Pin,
  PinOff,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatETLocale, formatET } from '../utils/timeUtils';
import DealProfitCalculator from '../components/DealProfitCalculator';
import DealExpenseLedger from '../components/DealExpenseLedger';
// v3.14-175: DealControlPanel REMOVED from Client Detail (lives only in PTC Deals)
// v3.14-175: DealNextAction REMOVED — Next Action in DealDashboard compact block
import ClientQuickView from '../components/ClientQuickView';
import DealDashboard from '../components/DealDashboard';  // v3.14-153: Block-based dashboard
import GhostContactAwarenessModal from '../components/GhostContactAwarenessModal';  // v3.15-1141
// v3.18-1250 CALL-TRACK-002 — retired RequiredCallNotesModal; the
// inbound-call disposition gate now uses ActivityCompletionDialog so
// every completable activity funnels through one framework.
import ActivityCompletionDialog from '../components/ActivityCompletionDialog';
// v3.15-1057: group assignee dropdowns by company
import { groupUsersByCompany } from '../utils/userGrouping';
import UserMultiSelect from '../components/UserMultiSelect';

// Simplified CRM Pipeline - single horizontal flow left-to-right
const stageColors = {
  new_application: 'bg-blue-100 text-blue-800',
  new_lead: 'bg-cyan-100 text-cyan-800',
  proposal_sent: 'bg-indigo-100 text-indigo-800',
  proposal_approved: 'bg-purple-100 text-purple-800',
  contract_sent: 'bg-violet-100 text-violet-800',
  contract_signed: 'bg-fuchsia-100 text-fuchsia-800',
  financing_submitted: 'bg-amber-100 text-amber-800',
  offers_received: 'bg-yellow-100 text-yellow-800',
  financing_approved: 'bg-lime-100 text-lime-800',
  funded: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-800',
  guest_did_not_use_financing: 'bg-gray-100 text-gray-800',
  no_offers_found: 'bg-orange-100 text-orange-800',
  // Legacy stages (for backwards compatibility)
  financing_approved_by_client: 'bg-teal-100 text-teal-800',
  bank_needs_docs: 'bg-orange-100 text-orange-800',
  final_review_by_lender: 'bg-pink-100 text-pink-800',
};

// Simplified pipeline stages - single row, left-to-right CRM flow
const stageOptions = [
  { value: 'new_application', label: 'New Application' },
  { value: 'new_lead', label: 'New Lead' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'proposal_approved', label: 'Proposal Approved' },
  { value: 'contract_sent', label: 'Contract Sent' },
  { value: 'contract_signed', label: 'Contract Signed' },
  { value: 'financing_submitted', label: 'Financing Submitted' },
  { value: 'offers_received', label: 'Offers Received' },
  { value: 'financing_approved', label: 'Financing Approved' },
  { value: 'funded', label: 'Funded' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'guest_did_not_use_financing', label: 'Guest Did Not Use Financing' },
  { value: 'no_offers_found', label: 'No Offers Found' },
];

// Funding status options — reuse stageOptions
const fundingStatusOptions = stageOptions;

const activityTypeIcons = {
  stage_change: TrendingUp,
  status_change: TrendingUp,
  note_added: FileText,
  internal_note_added: Lock,
  document_uploaded: Upload,
  task_created: ClipboardList,
  task_completed: CheckCircle,
  assignment_change: UserPlus,
  client_created: User,
  deal_updated: DollarSign,
  funding_updated: DollarSign,
  call_outcome_logged: PhoneOutgoing,
};

const activityTypeColors = {
  stage_change: 'bg-purple-100 text-purple-600',
  status_change: 'bg-purple-100 text-purple-600',
  note_added: 'bg-blue-100 text-blue-600',
  internal_note_added: 'bg-red-100 text-red-600',
  document_uploaded: 'bg-green-100 text-green-600',
  task_created: 'bg-amber-100 text-amber-600',
  task_completed: 'bg-emerald-100 text-emerald-600',
  assignment_change: 'bg-pink-100 text-pink-600',
  client_created: 'bg-slate-100 text-slate-600',
  deal_updated: 'bg-cyan-100 text-cyan-600',
  funding_updated: 'bg-orange-100 text-orange-600',
  call_outcome_logged: 'bg-indigo-100 text-indigo-600',
};

// v3.17-1241 (OPS-002 #3) — Return-to-previous-location trail.
// Rendered via a portal to <body> so it mounts AFTER Sonner's <Toaster />
// (which uses z-index=999999999). Inline style forces INT_MAX so no
// existing overlay (GhostContactAwarenessModal z=100001, Sonner
// z=999999999, RequiredCallNotesModal, etc.) can intercept the click.
// Operators must always be able to bail out per the OPS-002 directive.
const ReturnToTrail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.returnTo;
  const returnLabel = location.state?.returnLabel || 'previous location';
  if (!returnTo) return null;
  if (typeof document === 'undefined') return null;
  const banner = (
    <div
      data-testid="return-to-previous-trail"
      style={{ zIndex: 2147483647 }}
      className="fixed top-3 left-1/2 -translate-x-1/2
                 flex items-center gap-2 rounded-full
                 bg-primary text-white shadow-2xl border border-accent/60
                 pl-3 pr-1 py-1 pointer-events-auto"
    >
      <div className="text-[11px]">
        <span className="text-white/60">Opened from</span>{' '}
        <span className="font-semibold text-accent">{returnLabel}</span>
      </div>
      <button
        type="button"
        onClick={() => navigate(returnTo)}
        data-testid="return-to-previous-btn"
        className="inline-flex items-center gap-1 rounded-full bg-accent hover:bg-gold-dark
                   text-primary px-3 py-0.5 text-[11px] font-bold uppercase tracking-wider transition"
      >
        ← Return
      </button>
    </div>
  );
  return createPortal(banner, document.body);
};

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isManager, isInternal, canSendSms } = useAuth();

  // Helper function to mask SSN - REMOVED v3.15: No redaction for internal PTC users

  // Helper function to format employment length
  const formatEmploymentLength = (length) => {
    const formats = {
      'less_than_1_year': 'Less than 1 year',
      '1_3_years': '1-3 years',
      '3_5_years': '3-5 years',
      '5_plus_years': '5+ years',
    };
    return formats[length] || length || '-';
  };

  // Helper function to format residence type
  const formatResidenceType = (type) => {
    const formats = {
      'own': 'Own',
      'rent': 'Rent',
      'other': 'Other',
    };
    return formats[type] || type || '-';
  };

  // Helper function to format account type
  const formatAccountType = (type) => {
    const formats = {
      'checking': 'Checking',
      'savings': 'Savings',
    };
    return formats[type] || type || '-';
  };

  // Check if user can see PTC Payment Information
  // Only visible to: PTC Admin, External Company Director
  const canSeePtcPayment = () => {
    if (!user) return false;
    if (isInternal() && isAdmin()) return true; // PTC Admin
    if (!isInternal() && (user.role === 'company_director' || user.role === 'company_admin')) return true; // External Director/Admin
    return false;
  };

  // Calculate Days Outstanding
  const calculateDaysOutstanding = () => {
    if (!client?.ptc_payment_due_date) return null;
    // v3.14-102: Use settlement_status as ONLY truth for payment state
    if (client.settlement_status === 'settled') return 0;
    
    const dueDate = new Date(client.ptc_payment_due_date);
    const today = new Date();
    const diffTime = today - dueDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  // State declarations
  const [client, setClient] = useState(null);
  // v3.21-1805 · Monotonic generation ref for `fetchClient`.  Every
  // fire-and-forget refetch across the file (offer refresh, financing
  // submission callbacks, websocket, invoice creation, merge, etc.)
  // increments this before its GET.  Only the latest generation is
  // allowed to setClient(); older responses that land AFTER a newer
  // one are dropped on the floor.  This eliminates the authoritative
  // Application ID (`current_finturf_application_id`) getting erased
  // by a stale pre-save fetch that resolves after the PUT.
  const fetchClientGenRef = useRef(0);
  const [deal, setDeal] = useState(null);
  const [activities, setActivities] = useState([]);
  // v3.18-1260 PAYMENT-005 — receipt viewer state for timeline rows
  const [timelineReceiptTxId, setTimelineReceiptTxId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  // v3.15-670: Package Tracking state
  const [trackingData, setTrackingData] = useState(null);
  const [trackingInput, setTrackingInput] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [creatingTracker, setCreatingTracker] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [ptcReps, setPtcReps] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [lenderSearchOpen, setLenderSearchOpen] = useState(false);
  const [lenderSearchQuery, setLenderSearchQuery] = useState('');
  // v3.14-529: Company comms permission for external orgs
  const [commsEnabled, setCommsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // v3.15-1141: Ghost Contact Awareness — every mount fires once and blocks UI until acknowledged.
  const [ghostAcknowledged, setGhostAcknowledged] = useState(false);
  // v3.18-1249 BUG 2 — React Router reuses this component when the
  // `:id` route param changes (Ghost → new client, Client A → Client B),
  // so `ghostAcknowledged` was sticking `true` from a previous client
  // and the modal never re-appeared. Reset it whenever we open a
  // different client. This is the SAME open-flow used elsewhere; no
  // duplicate navigation logic — the reset simply forces the existing
  // modal-mount branch to re-fire per client id.
  useEffect(() => {
    setGhostAcknowledged(false);
  }, [id]);
  // v3.15-1150: Per-client pending inbound-call disposition gate. Fetched
  // after Ghost ack so the rep documents the call BEFORE working the file.
  const [pendingInboundDisposition, setPendingInboundDisposition] = useState(null);
  // v3.15-711: Live presence tracking
  const [viewers, setViewers] = useState([]);
  // v3.18-1348 LAC-FIX — track how many workflow claims are currently
  // visible so the generic "Also viewing" pill can defer to the far
  // more informative LIVE ACTIVITY banners. Per user directive:
  // workflow claims are operationally more important; the generic
  // viewer indicator should only appear when NO workflow claim is
  // active.
  const [activeWorkflowClaimCount, setActiveWorkflowClaimCount] = useState(0);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isTaskOpen, setIsTaskOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  // v3.15-739: Deal Ownership gate (PTC only) — modal state
  const [ownershipGateOpen, setOwnershipGateOpen] = useState(false);
  const [ownershipMissing, setOwnershipMissing] = useState([]);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [merging, setMerging] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteIsInternal, setNoteIsInternal] = useState(false);
  const [editData, setEditData] = useState({});
  // v3.14-165: Single unified view
  const [userPermissions, setUserPermissions] = useState({
    can_edit_commission_roles: false,
    can_view_commission_roles: false,
  });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    due_time: '',
  });

  // Confirm Funding Dialog State
  const [confirmFundingDialog, setConfirmFundingDialog] = useState(false);
  const [confirmFundingData, setConfirmFundingData] = useState({
    funding_date: new Date().toISOString().split('T')[0],
    funded_amount: '',
    confirmation_number: '',
    ach_confirmation_number: '',
  });
  const [confirmingFunding, setConfirmingFunding] = useState(false);

  // v3.14-100: Bill.com state REMOVED

  // v3.14-103: Next Action Panel State
  const [actionPanelData, setActionPanelData] = useState({
    amount_received: '',
    payment_type: '',
    ach_confirmation_number: '',
  });
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Financing Terms Bar State - Now supports two financing options
  const [financingOption1, setFinancingOption1] = useState({
    loan_amount_requested: '',
    approved_amount: '',
    term: '',
    rate: '',
    payment: '',
    funding_status: '',
  });
  const [financingOption2, setFinancingOption2] = useState({
    loan_amount_requested: '',
    approved_amount: '',
    term: '',
    rate: '',
    payment: '',
    funding_status: '',
  });
  // Legacy state for backwards compatibility
  const [financingTerms, setFinancingTerms] = useState({
    loan_amount_requested: '',
    approved_amount: '',
    term: '',
    rate: '',
    payment: '',
    funding_status: '',
  });
  const [savingFinancingTerms, setSavingFinancingTerms] = useState(false);
  const [savingOption1, setSavingOption1] = useState(false);
  const [savingOption2, setSavingOption2] = useState(false);

  // Financing Offers State
  const [financingOffers, setFinancingOffers] = useState([]);
  const [isAddOfferOpen, setIsAddOfferOpen] = useState(false);
  const [newOffer, setNewOffer] = useState({
    approved_amount: '',
    term: '',
    interest_rate: '',
    estimated_payment: '',
    lender_name: '',
    notes: '',
  });
  const [savingOffer, setSavingOffer] = useState(false);

  // Sensitive Data State (SSN + DOB)
  const [sensitiveData, setSensitiveData] = useState(null);
  const [revealingSensitive, setRevealingSensitive] = useState(false);
  const [sensitiveConfirm, setSensitiveConfirm] = useState(false);

  // Application ID Edit State
  const [isEditAppIdOpen, setIsEditAppIdOpen] = useState(false);
  const [editAppId, setEditAppId] = useState('');
  const [savingAppId, setSavingAppId] = useState(false);

  // Create Deal State
  const [showCreateDealDialog, setShowCreateDealDialog] = useState(false);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [newDealResorts, setNewDealResorts] = useState([{ name: '', price: '' }]);
  const [newDealNotes, setNewDealNotes] = useState('');
  // v3.15-927: Schedule Appointment modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [repBookingSlug, setRepBookingSlug] = useState('');
  const [copiedBookingLink, setCopiedBookingLink] = useState(false);
  // v3.15-981: gate the closure email behind a confirmation modal
  const [paidInFullConfirmOpen, setPaidInFullConfirmOpen] = useState(false);
  // v3.15-1069: Transactional SMS modal (TEST MODE default, admin-gated).
  const [txnSmsOpen, setTxnSmsOpen] = useState(false);
  const [txnSmsType, setTxnSmsType] = useState('credit_app');

  const [newDealCommission, setNewDealCommission] = useState({
    sales_rep_id: null,
    manager_id: null,
    director_id: null,
    payment_method: null,
  });

  // v3.14-175: Collapsible states for PTC sections
  const [ptcProgressExpanded, setPtcProgressExpanded] = useState(false);
  const [profitCalcExpanded, setProfitCalcExpanded] = useState(false);

  // v3.14-179: Unified bottom section tab state
  // v3.21-1816 EMERGENCY — the v3.21-1815 deep-link addition read
  // ``searchParams`` without ever destructuring the ``useSearchParams``
  // hook, crashing the CRM panel with ``searchParams is not defined``.
  // Restore the hook AND keep the intended ``?tab=<name>`` behavior.
  const [searchParams] = useSearchParams();
  const _initialTab = ((searchParams && searchParams.get && searchParams.get('tab')) || '').trim();
  const [activeBottomTab, setActiveBottomTab] = useState(_initialTab || 'activity');
  useEffect(() => {
    const wanted = ((searchParams && searchParams.get && searchParams.get('tab')) || '').trim();
    if (wanted && wanted !== activeBottomTab) setActiveBottomTab(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // v3.21-1746 · Action last-dates for the operational reaction
  // buttons rendered inside the Activity toolbar below (Mark as At
  // Risk / Mark Hard to Reach / Let's Get This Deal / Clear To
  // Collect).  Reads the Activity SSOT via
  //   GET /api/clients/{id}/action-last-dates
  // which aggregates the canonical `activities` collection — the
  // same source of truth that feeds the visible Activity tab.
  const [actionLastDates, setActionLastDates] = useState(null);
  const refreshActionLastDates = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/clients/${id}/action-last-dates`);
      setActionLastDates(data?.last_dates || null);
    } catch (_e) { /* soft-fail; chip renders — */ }
  }, [id]);
  useEffect(() => { refreshActionLastDates(); }, [refreshActionLastDates, client?.updated_at, client?.last_rallied_at, client?.at_risk_manual, client?.is_hard_to_reach]);
  const fmtDateChip = useCallback((iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }); }
    catch { return '—'; }
  }, []);
  const dispatchDealAction = useCallback((action) => {
    if (!id) return;
    window.dispatchEvent(new CustomEvent('hub:deal-action', { detail: { action, clientId: id } }));
    setTimeout(() => refreshActionLastDates(), 800);
  }, [id, refreshActionLastDates]);
  // v3.21-1638 · Block 5 — Per-user tab attention.  Fetched from the
  // canonical /clients/{id}/tab-attention endpoint (SSOT: db.activities
  // vs db.client_tab_seen, self-action excluded).  A tab blinks until
  // the CURRENT user opens it; opening dispatches mark-seen and clears
  // that tab's blink for THIS user only.
  const [tabAttention, setTabAttention] = useState({});
  const fetchTabAttention = useCallback(async () => {
    if (!client?.id) return;
    try {
      const r = await api.get(`/clients/${client.id}/tab-attention`);
      setTabAttention(r.data?.tabs || {});
    } catch (_) { /* non-fatal */ }
  }, [client?.id]);
  useEffect(() => { fetchTabAttention(); }, [fetchTabAttention, activities.length, tasks.length, documents.length]);
  const markTabSeen = useCallback(async (tab) => {
    if (!client?.id) return;
    try {
      await api.post(`/clients/${client.id}/tab-attention/mark-seen`, { tab });
      setTabAttention(prev => ({ ...prev, [tab]: { ...(prev[tab]||{}), unseen: 0, blink: false } }));
    } catch (_) { /* non-fatal */ }
  }, [client?.id]);
  const onTabChange = useCallback((next) => {
    setActiveBottomTab(next);
    markTabSeen(next);
  }, [markTabSeen]);
  // v3.21-1813 · Smart Client Communication Awareness — canonical
  // count + per-user NEW badge + Last Communication payload.  Powered
  // by GET /api/clients/{id}/communication-summary which lazily
  // initializes a per-user baseline the FIRST time this user opens
  // the client (so historical archives never masquerade as NEW).
  const [commSummary, setCommSummary] = useState(null);
  const fetchCommSummary = useCallback(async () => {
    if (!client?.id) return;
    try {
      const r = await api.get(`/clients/${client.id}/communication-summary`);
      setCommSummary(r.data || null);
    } catch (_) { /* non-fatal */ }
  }, [client?.id]);
  useEffect(() => { fetchCommSummary(); }, [fetchCommSummary, activities.length]);
  const openCommunicationHistory = useCallback(async () => {
    setActiveBottomTab('communications');
    if (!client?.id) return;
    try {
      // Preserve existing tab-attention mark-seen (other tabs rely on
      // it), AND record the user-specific Communication History view
      // via the dedicated per-tab, per-user endpoint.
      await Promise.all([
        api.post(`/clients/${client.id}/tab-attention/mark-seen`, { tab: 'communications' }),
        api.post(`/clients/${client.id}/communication-summary/mark-viewed`),
      ]);
      setCommSummary(prev => (prev ? { ...prev, unread_count: 0 } : prev));
      setTabAttention(prev => ({ ...prev, communications: { ...(prev?.communications || {}), unseen: 0, blink: false } }));
    } catch (_) { /* non-fatal */ }
  }, [client?.id]);
  const blinkCls = (tab) => (tabAttention?.[tab]?.blink
    ? 'relative animate-pulse text-red-700 font-semibold data-[state=active]:text-red-700'
    : '');

  // v3.15-1224 OWNERSHIP-001 — resolve the client's company industry_category
  // once, so we know whether to render the "Ownership Information" tab.
  const [companyIndustryCategory, setCompanyIndustryCategory] = useState(null);
  useEffect(() => {
    let alive = true;
    const cid = client?.company_id;
    if (!cid) { setCompanyIndustryCategory(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/companies/${cid}`);
        if (alive) setCompanyIndustryCategory((data?.industry_category || data?.company?.industry_category || '').toLowerCase());
      } catch {
        if (alive) setCompanyIndustryCategory(null);
      }
    })();
    return () => { alive = false; };
  }, [client?.company_id]);
  const isTimeshareExitCompany = companyIndustryCategory === 'timeshare_exit';

  // v3.21-1705 — Ownership Information visibility correction.
  // Prior gate tied visibility strictly to `industry_category==='timeshare_exit'`,
  // which hid the tab from authorized internal PTC users whose clients live
  // under internal PTC/EQF companies (e.g. legacy timeshare owners imported
  // via Paste Client Information). New rule:
  //   • Internal PTC users → always eligible (per-client access already
  //     enforced by SECURITY-001 filter_clients_by_access when they loaded
  //     this client; backend ownership routes re-check).
  //   • External partners → only when the client's company remains
  //     industry_category === 'timeshare_exit' (preserves external isolation).
  //   • Any user viewing a client that already has ownership records
  //     → show the tab so they can inspect/manage the data. Backend still
  //     rejects cross-company reads for unauthorized externals.
  const [hasOwnershipRecords, setHasOwnershipRecords] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!id) { setHasOwnershipRecords(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/clients/${id}/ownerships`);
        const list = Array.isArray(data) ? data : (data?.items || data?.ownerships || []);
        if (alive) setHasOwnershipRecords(Array.isArray(list) && list.length > 0);
      } catch { if (alive) setHasOwnershipRecords(false); }
    })();
    return () => { alive = false; };
  }, [id]);
  const canSeeOwnershipTab = (
    (typeof isInternal === 'function' ? isInternal() : false)
    || isTimeshareExitCompany
    || hasOwnershipRecords
  );

  // v3.14-184: Notes state (moved from DealDashboard to unified section)
  const [editingNotes, setEditingNotes] = useState(false);
  const [externalNotesEdit, setExternalNotesEdit] = useState('');
  const [internalNotesEdit, setInternalNotesEdit] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  // v3.14-529: Add Note (non-admin users) — activity-tracked note
  const [addNoteContent, setAddNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [rewritingNote, setRewritingNote] = useState(false);
  // v3.21-1685 · Operator-chosen attention color at note create time.
  // Defaults to neutral.  Pure attention — never alters Deal lifecycle.
  const [addNoteColor, setAddNoteColor] = useState('neutral');

  // v3.15-1124 — Pinned / Edit / Delete notes state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  // v3.21-1667 · Attention color may be edited in-place alongside content.
  const [editingNoteColor, setEditingNoteColor] = useState('neutral');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [pinningNoteId, setPinningNoteId] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const isPtcAdmin = user?.role === 'ptc_admin';

  // v3.15-745: Deal Ownership popup — non-blocking reminder for PTC users.
  // Triggers on load when `_ownership_complete === false`. Shown once per
  // session per deal (sessionStorage key). Editing remains fully unblocked.
  useEffect(() => {
    if (!client || !user) return;
    const isPtc = String(user?.role || '').startsWith('ptc_');
    if (!isPtc) return;
    if (client._ownership_complete !== false) return;
    try {
      const key = `deal-ownership-popup-seen:${id}`;
      if (sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
    } catch (_) { /* sessionStorage may be unavailable */ }
    setOwnershipMissing(
      Array.isArray(client._ownership_missing_fields) && client._ownership_missing_fields.length > 0
        ? client._ownership_missing_fields
        : ['Company', 'Director', 'Manager', 'Sales Rep']
    );
    setOwnershipGateOpen(true);
  }, [client, user, id]);

  // v3.15-1150: Check for pending inbound-call dispositions for THIS
  // client + THIS rep AFTER Ghost ack. Forces documentation BEFORE the
  // file becomes editable.
  const refetchPendingInbound = useCallback(async () => {
    if (!id || !ghostAcknowledged) return;
    try {
      const { data } = await api.get(`/inbound-calls/pending-dispositions/by-client/${id}`);
      const items = data?.items || [];
      setPendingInboundDisposition(items.length > 0 ? items[items.length - 1] : null);
    } catch (_e) {
      setPendingInboundDisposition(null);
    }
  }, [id, ghostAcknowledged]);

  useEffect(() => {
    refetchPendingInbound();
  }, [refetchPendingInbound]);

  // v3.15-927: Fetch the current user's booking slug so the header's "Copy
  // Booking Link" button can emit the correct public URL.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get('/appointments/me/settings');
        setRepBookingSlug(data?.settings?.booking_slug || '');
      } catch { /* silent */ }
    })();
  }, [user]);

  const handleAddActivityNote = async () => {
    if (!addNoteContent.trim()) return;
    setAddingNote(true);
    try {
      // v3.15-751: AI Ghost rewrite is a SEPARATE user action (handleGhostRewrite).
      // "Add Note" posts the current input as-is — no auto-rewrite, no surprise edits.
      const finalContent = addNoteContent.trim();
      // v3.21-1685 · Persist operator-chosen attention_color at create
      // time.  Backend normalizes/validates canonical values.
      await api.post(`/clients/${id}/notes`, {
        content: finalContent,
        is_internal: noteIsInternal,
        attention_color: addNoteColor || 'neutral',
      });
      toast.success('Note added');
      setAddNoteContent('');
      setNoteIsInternal(false);
      setAddNoteColor('neutral');
      fetchActivities();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  // v3.15-1124 — Pinned / Edit / Delete note handlers
  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.description || note.content || '');
    // v3.21-1667 · seed color picker with the note's current attention_color.
    setEditingNoteColor(normalizeAttentionColor(note.attention_color));
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
    setEditingNoteColor('neutral');
  };

  const handleSaveEditNote = async (noteId) => {
    const content = (editingNoteContent || '').trim();
    if (!content) {
      toast.error('Note content cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      // v3.21-1667 · Persist attention_color alongside content in the
      // same PATCH so operators never have to delete/recreate a note
      // to change its color.  Backend enforces the canonical vocabulary
      // and never mutates Deal financial fields.
      await api.patch(`/clients/${id}/notes/${noteId}`, {
        content,
        attention_color: normalizeAttentionColor(editingNoteColor),
      });
      toast.success('Note updated');
      setEditingNoteId(null);
      setEditingNoteContent('');
      setEditingNoteColor('neutral');
      fetchActivities();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to edit note');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    setDeletingNoteId(noteId);
    try {
      await api.delete(`/clients/${id}/notes/${noteId}`);
      toast.success('Note deleted');
      setNoteToDelete(null);
      fetchActivities();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleTogglePinNote = async (note) => {
    setPinningNoteId(note.id);
    try {
      const nextPinned = !note.is_pinned;
      await api.post(`/clients/${id}/notes/${note.id}/pin`, { pinned: nextPinned });
      toast.success(nextPinned ? 'Note pinned' : 'Note unpinned');
      fetchActivities();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to pin note');
    } finally {
      setPinningNoteId(null);
    }
  };

  const canModifyNote = (note) => {
    if (!note || !user) return false;
    // Only user-created notes are modifiable
    const editableTypes = ['note', 'note_added', 'internal_note_added'];
    const t = note.activity_type || note.type;
    if (!editableTypes.includes(t)) return false;
    // Admin can modify any note
    const role = (user.role || '').toLowerCase();
    if (['ptc_admin', 'admin', 'super_admin'].includes(role)) return true;
    // Otherwise must be the author
    const myId = user.user_id || user.id;
    const myEmail = (user.email || '').toLowerCase();
    return (note.user_id && note.user_id === myId) ||
           ((note.user_email || '').toLowerCase() === myEmail);
  };

  // v3.15-751: Ghost Rewrite — draft-only action. Replaces the input text with
  // the AI-rewritten version, does NOT post. User reviews, edits, then clicks "Add Note".
  const addNoteInputRef = useRef(null);
  const addNoteTextareaRef = useRef(null);
  const handleGhostRewrite = async (focusTarget = 'input') => {
    const draft = (addNoteContent || '').trim();
    if (!draft) {
      toast.error('Type a draft first — Ghost will polish it');
      return;
    }
    setRewritingNote(true);
    try {
      const ghostRes = await api.post('/ghost-ops/rewrite-note', {
        content: draft,
        client_name: client?.full_name || '',
      });
      const rewritten = ghostRes.data?.rewritten;
      if (rewritten && rewritten.trim()) {
        setAddNoteContent(rewritten);
        toast.success('Ghost draft ready — review, edit, then Add Note');
        setTimeout(() => {
          const el = focusTarget === 'textarea' ? addNoteTextareaRef.current : addNoteInputRef.current;
          if (el) { el.focus(); try { el.setSelectionRange(rewritten.length, rewritten.length); } catch (e) { console.warn('[ClientDetail.GhostDraft] setSelectionRange failed', e); } }
        }, 30);
      } else {
        toast.error('Ghost returned no text — keep your draft');
      }
    } catch {
      toast.error('Ghost rewrite failed — keep your draft');
    } finally {
      setRewritingNote(false);
    }
  };

  const handleStartEditNotes = () => {
    setExternalNotesEdit(client?.external_notes || '');
    setInternalNotesEdit(client?.internal_notes || '');
    setEditingNotes(true);
  };
  const handleCancelEditNotes = () => setEditingNotes(false);

  // v3.14-435: Click-to-Call
  const [calling, setCalling] = useState(false);
  const [callAs, setCallAs] = useState('eqf_hub');
  // v3.21-1612 · Post-call outcome tracking removed — ActiveCallPill
  // handles disposition + notes + End Call for every Twilio call.
  const handleClickToCall = async () => {
    if (!client?.phone) { toast.error('Client has no phone number'); return; }
    const digits = (client.phone || '').replace(/\D/g, '');
    if (digits.length < 10) { toast.error('Invalid phone number format'); return; }
    if (!user) { toast.error('You must be signed in to place a call'); return; }
    if (calling) return;
    // v3.21-1835 · explicit confirmation before any manual outbound call
    if (!(await requestCallConfirmation({ name: client?.full_name, phone: client.phone }))) return;
    setCalling(true);
    try {
      const { data: conf } = await api.post('/telephony/call-confirmations', {
        phone_number: client.phone, client_id: id, client_name: client?.full_name,
        source_page: window.location.pathname, transport: 'phone_bridge',
      });
      const { data } = await api.post('/telephony/call', {
        client_number: client.phone,
        client_id: id,
        client_name: client?.full_name,
        call_as: callAs,
        confirmation_id: conf?.confirmation_id,
      });
      toast.success(data.message || 'Call initiated');
      fetchActivities();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Call failed';
      toast.error(typeof msg === 'string' ? msg : 'Call failed');
    } finally {
      setCalling(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/clients/${id}`, {
        external_notes: externalNotesEdit,
        internal_notes: internalNotesEdit,
      });
      toast.success('Notes saved');
      setEditingNotes(false);
      fetchClient();
    } catch (err) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Check if user can reveal SSN (only internal: ptc_admin, ptc_manager, ptc_rep)
  const canRevealSSN = () => {
    if (!user || !isInternal()) return false;
    return ['ptc_admin', 'ptc_director', 'ptc_manager', 'ptc_rep'].includes(user.role);
  };

  // Check if user is a sales rep (restricted external user)
  const isSalesRep = () => {
    return user?.role === 'company_team_member';
  };

  // Check if user can view credit application details
  // Sales reps cannot see credit details unless granted permission
  const canViewCreditDetails = () => {
    if (isInternal()) return true;
    if (isSalesRep()) {
      return user?.permissions?.can_view_credit_details === true;
    }
    // Company Directors and Admins can view
    return ['company_director', 'company_admin'].includes(user?.role);
  };

  // Check if user can approve/decline financing
  // Sales reps cannot approve/decline unless granted permission
  const canApproveFinancing = () => {
    if (isInternal()) return false; // Internal users save, not approve
    if (isSalesRep()) {
      return user?.permissions?.can_approve_financing === true;
    }
    // Company Directors and Managers can approve
    return ['company_director', 'company_admin', 'company_manager'].includes(user?.role);
  };

  // ==================== PERMISSION LOCKDOWN FUNCTIONS ====================
  
  // Check if user can EDIT Deal Information (stage, amounts, rates)
  // ONLY PTC Director and Admin can edit
  const canEditDealInfo = () => {
    if (!user) return false;
    return ['ptc_admin', 'ptc_director'].includes(user.role);
  };

  // Check if user can EDIT Funding Status/Amount
  // ONLY PTC Director and Admin can edit
  const canEditFundingInfo = () => {
    if (!user) return false;
    return ['ptc_admin', 'ptc_director'].includes(user.role);
  };

  // Check if user can EDIT PTC Payment Information
  // ONLY PTC Director and Admin can edit
  const canEditPtcPayment = () => {
    if (!user) return false;
    return ['ptc_admin', 'ptc_director'].includes(user.role);
  };

  // Check if user is an External Partner (company_director, company_team_member, etc.)
  // External partners should NOT see Deal, Funding, or PTC Payment sections in Edit modal
  const isExternalPartner = () => {
    if (!user) return false;
    return ['company_director', 'company_admin', 'company_manager', 'company_team_member'].includes(user.role);
  };

  // Check if user can VIEW financial sections (non-edit view)
  // Internal users can always view, external directors can view (read-only)
  const canViewFinancialSections = () => {
    if (!user) return false;
    if (isInternal()) return true;
    // External directors can VIEW but not edit
    return ['company_director', 'company_admin'].includes(user.role);
  };

  // ==================== END PERMISSION LOCKDOWN ====================

  // Initialize financing terms when client loads
  useEffect(() => {
    if (client) {
      // Load Option 1 (primary - from client record)
      setFinancingOption1({
        loan_amount_requested: client.loan_amount_requested || '',
        approved_amount: client.approved_amount || '',
        term: client.term || '',
        rate: client.rate || '',
        payment: client.payment || '',
        funding_status: client.funding_status || '',
      });
      // Load Option 2 (from financing_option_2 field if exists)
      setFinancingOption2({
        loan_amount_requested: client.financing_option_2?.loan_amount_requested || '',
        approved_amount: client.financing_option_2?.approved_amount || '',
        term: client.financing_option_2?.term || '',
        rate: client.financing_option_2?.rate || '',
        payment: client.financing_option_2?.payment || '',
        funding_status: client.financing_option_2?.funding_status || '',
      });
      // Legacy support
      setFinancingTerms({
        loan_amount_requested: client.loan_amount_requested || '',
        approved_amount: client.approved_amount || '',
        term: client.term || '',
        rate: client.rate || '',
        payment: client.payment || '',
        funding_status: client.funding_status || '',
      });
      // Reset sensitive data when client changes
      setSensitiveData(null);
      setSensitiveConfirm(false);
    }
  }, [client]);

  // v3.14-179: Fetch activities (was missing — called from many handlers)
  const fetchActivities = async () => {
    try {
      const response = await api.get(`/clients/${id}/activities`);
      setActivities(response.data || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  // v3.15-580: Listen for call-outcome-logged events to refresh activity feed
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.clientId === id || !e.detail?.clientId) {
        fetchActivities();
      }
    };
    window.addEventListener('call-outcome-logged', handler);
    return () => window.removeEventListener('call-outcome-logged', handler);
  }, [id]);

  // Fetch financing offers
  const fetchFinancingOffers = async () => {
    try {
      const response = await api.get(`/clients/${id}/financing-offers`);
      setFinancingOffers(response.data);
    } catch (error) {
      console.error('Error fetching financing offers:', error);
    }
  };

  // Add financing offer handler
  const handleAddOffer = async () => {
    if (!newOffer.approved_amount || !newOffer.term || !newOffer.interest_rate || !newOffer.estimated_payment) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSavingOffer(true);
    try {
      await api.post(`/clients/${id}/financing-offers`, {
        approved_amount: parseFloat(newOffer.approved_amount),
        term: newOffer.term,
        interest_rate: parseFloat(newOffer.interest_rate),
        estimated_payment: parseFloat(newOffer.estimated_payment),
        lender_name: newOffer.lender_name || null,
        notes: newOffer.notes || null,
      });
      toast.success('Financing offer added');
      setIsAddOfferOpen(false);
      setNewOffer({ approved_amount: '', term: '', interest_rate: '', estimated_payment: '', lender_name: '', notes: '' });
      fetchFinancingOffers();
      fetchActivities();
    } catch (error) {
      toast.error('Failed to add financing offer');
    } finally {
      setSavingOffer(false);
    }
  };

  // Select financing offer handler
  const handleSelectOffer = async (offerId) => {
    try {
      const response = await api.post(`/clients/${id}/financing-offers/${offerId}/select`);
      toast.success('Financing offer selected and terms applied');
      // Update financing terms bar with selected offer
      setFinancingTerms({
        ...financingTerms,
        approved_amount: response.data.approved_amount,
        term: response.data.term,
        rate: response.data.rate,
        payment: response.data.payment,
      });
      fetchClient();
      fetchFinancingOffers();
      fetchActivities();
    } catch (error) {
      toast.error('Failed to select financing offer');
    }
  };

  // Delete financing offer handler
  const handleDeleteOffer = async (offerId) => {
    try {
      await api.delete(`/clients/${id}/financing-offers/${offerId}`);
      toast.success('Financing offer deleted');
      fetchFinancingOffers();
    } catch (error) {
      toast.error('Failed to delete financing offer');
    }
  };

  // Reveal Sensitive Data handler (SSN + DOB)
  const handleRevealSensitive = async () => {
    if (!sensitiveConfirm) {
      setSensitiveConfirm(true);
      return;
    }
    setRevealingSensitive(true);
    try {
      const response = await api.get(`/clients/${id}/sensitive`);
      setSensitiveData(response.data);
      toast.success('Sensitive data revealed - access logged');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to access sensitive data');
    } finally {
      setRevealingSensitive(false);
    }
  };

  // Credit Application Dialog State
  const [isAddCreditAppOpen, setIsAddCreditAppOpen] = useState(false);
  const [creditAppData, setCreditAppData] = useState({
    full_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    ssn: '',
    date_of_birth: '',
    monthly_income: '',
    employment_length: '',
    residence_type: '',
    loan_amount_requested: '',
    loan_purpose: '',
    source_rep_name: '',
    source_rep_email: '',
  });
  const [savingCreditApp, setSavingCreditApp] = useState(false);

  // Initialize credit app data when dialog opens
  useEffect(() => {
    if (isAddCreditAppOpen && client) {
      setCreditAppData({
        full_name: client.full_name || '',
        email: client.email || '',
        phone: client.phone || '',
        street_address: client.street_address || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip_code || '',
        ssn: '',
        date_of_birth: '',
        monthly_income: client.monthly_income || '',
        employment_length: client.employment_length || '',
        residence_type: client.residence_type || '',
        loan_amount_requested: client.loan_amount_requested || '',
        loan_purpose: client.loan_purpose || '',
        source_rep_name: client.source_rep_name || '',
        source_rep_email: client.source_rep_email || '',
      });
    }
  }, [isAddCreditAppOpen, client]);

  // Save Credit Application handler
  const handleSaveCreditApp = async () => {
    setSavingCreditApp(true);
    try {
      await api.put(`/clients/${id}`, {
        full_name: creditAppData.full_name || client.full_name,
        email: creditAppData.email || client.email,
        phone: creditAppData.phone || client.phone,
        street_address: creditAppData.street_address || null,
        city: creditAppData.city || null,
        state: creditAppData.state || null,
        zip_code: creditAppData.zip_code || null,
        ssn: creditAppData.ssn || null,
        date_of_birth: creditAppData.date_of_birth || null,
        monthly_income: creditAppData.monthly_income ? parseFloat(creditAppData.monthly_income) : null,
        employment_length: creditAppData.employment_length || null,
        residence_type: creditAppData.residence_type || null,
        loan_amount_requested: creditAppData.loan_amount_requested ? parseFloat(creditAppData.loan_amount_requested) : null,
        loan_purpose: creditAppData.loan_purpose || null,
        source_rep_name: creditAppData.source_rep_name || null,
        source_rep_email: creditAppData.source_rep_email || null,
      });
      toast.success('Credit application saved');
      setIsAddCreditAppOpen(false);
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error('Failed to save credit application');
    } finally {
      setSavingCreditApp(false);
    }
  };

  // Save financing terms handler
  const handleSaveFinancingTerms = async () => {
    setSavingFinancingTerms(true);
    try {
      // v3.15-1068 (Tier 3) — gate financial-field changes with operator confirm.
      const body = {
        loan_amount_requested: financingTerms.loan_amount_requested ? parseFloat(financingTerms.loan_amount_requested) : null,
        approved_amount: financingTerms.approved_amount ? parseFloat(financingTerms.approved_amount) : null,
        term: financingTerms.term || null,
        rate: financingTerms.rate ? parseFloat(financingTerms.rate) : null,
        payment: financingTerms.payment ? parseFloat(financingTerms.payment) : null,
        funding_status: financingTerms.funding_status || null,
      };
      const gate = confirmFinancialChanges(client, body);
      if (!gate.ok) {
        toast.info('Save cancelled — financial values were not changed.');
        setSavingFinancingTerms(false);
        return;
      }
      await api.put(`/clients/${id}`, gate.payload);
      toast.success('Financing terms saved');
      fetchClient();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save financing terms'));
    } finally {
      setSavingFinancingTerms(false);
    }
  };

  // Save Financing Option 1
  const handleSaveOption1 = async () => {
    setSavingOption1(true);
    try {
      // v3.15-1068 (Tier 3) — Option 1 writes top-level approved_amount,
      // so we gate it the same way as handleSaveFinancingTerms.
      const body = {
        loan_amount_requested: financingOption1.loan_amount_requested ? parseFloat(financingOption1.loan_amount_requested) : null,
        approved_amount: financingOption1.approved_amount ? parseFloat(financingOption1.approved_amount) : null,
        term: financingOption1.term || null,
        rate: financingOption1.rate ? parseFloat(financingOption1.rate) : null,
        payment: financingOption1.payment ? parseFloat(financingOption1.payment) : null,
        funding_status: financingOption1.funding_status || null,
      };
      const gate = confirmFinancialChanges(client, body);
      if (!gate.ok) {
        toast.info('Save cancelled — Option 1 financial values were not changed.');
        setSavingOption1(false);
        return;
      }
      await api.put(`/clients/${id}`, gate.payload);
      toast.success('Option 1 saved');
      fetchClient();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save Option 1'));
    } finally {
      setSavingOption1(false);
    }
  };

  // Save Financing Option 2
  const handleSaveOption2 = async () => {
    setSavingOption2(true);
    try {
      await api.put(`/clients/${id}`, {
        financing_option_2: {
          loan_amount_requested: financingOption2.loan_amount_requested ? parseFloat(financingOption2.loan_amount_requested) : null,
          approved_amount: financingOption2.approved_amount ? parseFloat(financingOption2.approved_amount) : null,
          term: financingOption2.term || null,
          rate: financingOption2.rate ? parseFloat(financingOption2.rate) : null,
          payment: financingOption2.payment ? parseFloat(financingOption2.payment) : null,
          funding_status: financingOption2.funding_status || null,
        },
      });
      toast.success('Option 2 saved');
      fetchClient();
    } catch (error) {
      toast.error('Failed to save Option 2');
    } finally {
      setSavingOption2(false);
    }
  };

  // Accept Option 1
  const handleAcceptOption1 = async () => {
    if (!financingOption1.approved_amount) {
      toast.error('Please enter an approved amount for Option 1');
      return;
    }
    setApprovingTerms(true);
    try {
      await api.post(`/clients/${id}/approve-terms`, {
        approved_amount: parseFloat(financingOption1.approved_amount),
        term: financingOption1.term || null,
        rate: financingOption1.rate ? parseFloat(financingOption1.rate) : null,
        payment: financingOption1.payment ? parseFloat(financingOption1.payment) : null,
        option_selected: 1,
      });
      toast.success('Option 1 accepted! PTC team has been notified.');
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept Option 1');
    } finally {
      setApprovingTerms(false);
    }
  };

  // Accept Option 2
  const handleAcceptOption2 = async () => {
    if (!financingOption2.approved_amount) {
      toast.error('Please enter an approved amount for Option 2');
      return;
    }
    setApprovingTerms(true);
    try {
      await api.post(`/clients/${id}/approve-terms`, {
        approved_amount: parseFloat(financingOption2.approved_amount),
        term: financingOption2.term || null,
        rate: financingOption2.rate ? parseFloat(financingOption2.rate) : null,
        payment: financingOption2.payment ? parseFloat(financingOption2.payment) : null,
        option_selected: 2,
      });
      toast.success('Option 2 accepted! PTC team has been notified.');
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept Option 2');
    } finally {
      setApprovingTerms(false);
    }
  };

  // Decline Option (same for both)
  const handleDeclineOption = async () => {
    if (!window.confirm('Are you sure you want to decline all financing options? The PTC team will be notified.')) {
      return;
    }
    setDecliningTerms(true);
    try {
      await api.post(`/clients/${id}/decline-terms`);
      toast.success('Financing declined. PTC team has been notified.');
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setDecliningTerms(false);
    }
  };

  // Partner Approve/Decline handlers
  const [approvingTerms, setApprovingTerms] = useState(false);
  const [decliningTerms, setDecliningTerms] = useState(false);

  const handleApproveTerms = async () => {
    if (!financingTerms.approved_amount) {
      toast.error('Please enter an approved amount before approving');
      return;
    }
    setApprovingTerms(true);
    try {
      await api.post(`/clients/${id}/approve-terms`, {
        approved_amount: parseFloat(financingTerms.approved_amount),
        term: financingTerms.term || null,
        rate: financingTerms.rate ? parseFloat(financingTerms.rate) : null,
        payment: financingTerms.payment ? parseFloat(financingTerms.payment) : null,
      });
      toast.success('Terms approved! PTC team has been notified.');
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve terms');
    } finally {
      setApprovingTerms(false);
    }
  };

  const handleDeclineTerms = async () => {
    if (!window.confirm('Are you sure you want to decline these financing terms? The PTC team will be notified.')) {
      return;
    }
    setDecliningTerms(true);
    try {
      await api.post(`/clients/${id}/decline-terms`);
      toast.success('Terms declined. PTC team has been notified.');
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline terms');
    } finally {
      setDecliningTerms(false);
    }
  };

  // Handle Application ID save
  const handleSaveAppId = async () => {
    setSavingAppId(true);
    try {
      // v3.14-151: Save Application ID and all Transaction Identifiers
      await api.put(`/clients/${id}`, { 
        application_id: editAppId,
        credit_app_confirmation_number: editData.credit_app_confirmation_number || null,
        ach_signature_confirmation: editData.ach_signature_confirmation || null,
        ach_bank_confirmation_number: editData.ach_bank_confirmation_number || null,
        wire_transfer_confirmation_number: editData.wire_transfer_confirmation_number || null,
      });
      toast.success('Application ID & Transaction Identifiers updated');
      setIsEditAppIdOpen(false);
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingAppId(false);
    }
  };

  // Initialize Application ID when client loads
  useEffect(() => {
    if (client) {
      setEditAppId(client.application_id || '');
    }
  }, [client]);

  // Format phone number for display and create tel: link
  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    // Remove all non-digit characters for the tel: link
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly;
  };

  // Component for clickable phone numbers
  // v3.14-109: PhoneLink replaced with PhoneClickable for QR modal functionality
  const PhoneLink = ({ phone, className = '' }) => {
    if (!phone) return <span className={className}>-</span>;
    return (
      <PhoneClickable 
        phone={phone} 
        name={client?.full_name}
        clientId={id}
        clientName={client?.full_name}
        className={className}
      />
    );
  };

  // Handle lender selection and auto-populate contact info
  const handleLenderSelect = async (lenderName) => {
    setEditData(prev => ({ ...prev, lending_institution: lenderName }));
    setLenderSearchOpen(false);
    setLenderSearchQuery('');
    
    // Find lender in directory and auto-populate only if fields are empty
    const lender = lenders.find(l => l.lender_name === lenderName);
    if (lender) {
      setEditData(prev => ({
        ...prev,
        lending_institution: lenderName,
        // Only populate if field is empty (don't overwrite existing values)
        lender_contact_name: prev.lender_contact_name || lender.contact_name || '',
        lender_contact_phone: prev.lender_contact_phone || lender.contact_phone || '',
        lender_contact_email: prev.lender_contact_email || lender.contact_email || '',
      }));
      toast.success(`Lender contact info populated for ${lenderName}`);
    }
  };

  // Filter lenders based on search query
  const filteredLenders = lenders.filter(l => 
    l.lender_name.toLowerCase().includes(lenderSearchQuery.toLowerCase())
  );

  // v3.15-739: Deal Ownership Gate (PTC users only) — central error handler.
  // Returns true when the error was an ownership-gate violation and was handled.
  const handleOwnershipGateError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === 'object' && detail.error === 'DEAL_OWNERSHIP_REQUIRED') {
      setOwnershipMissing(detail.missing_fields || ['Company', 'Director', 'Manager', 'Sales Rep']);
      setOwnershipGateOpen(true);
      return true;
    }
    return false;
  };

  const fetchClient = useCallback(async () => {
    // v3.21-1805 · Generation guard — capture the token for this call.
    // Increments happen at every entry so concurrent stale-fetch
    // responses cannot clobber newer authoritative state.
    const myGen = ++fetchClientGenRef.current;
    // v3.14-109: Use Promise.allSettled to handle individual failures gracefully
    // ONLY redirect if PRIMARY client fetch fails - secondary failures should NOT block page load
    try {
      // PRIMARY fetch - this is the only one that should cause redirect on failure
      const clientRes = await api.get(`/clients/${id}`);
      // v3.21-1805 · Drop the response entirely if a newer refetch has
      // already been issued while this GET was in flight.  Prevents a
      // pre-KEV26480 snapshot from erasing the just-saved canonical
      // Application ID (or any other subsequently-saved field).
      if (myGen !== fetchClientGenRef.current) {
        console.log('[ClientDetailPage v3.21-1805] Stale fetchClient response dropped (gen=%s current=%s)', myGen, fetchClientGenRef.current);
        return;
      }
      // v3.15-579: PROD CLIENT DATA — full object dump for production debugging
      console.log("PROD CLIENT DATA:", clientRes.data);
      console.log("PROD CLIENT DATA FIELDS:", JSON.stringify({
        id: clientRes.data?.id,
        full_name: clientRes.data?.full_name,
        street_address: clientRes.data?.street_address || 'EMPTY',
        city: clientRes.data?.city || 'EMPTY',
        state: clientRes.data?.state || 'EMPTY',
        zip_code: clientRes.data?.zip_code || 'EMPTY',
        ssn: clientRes.data?.ssn ? '***SET***' : 'EMPTY',
        has_ssn: clientRes.data?.has_ssn,
        ssn_last4: clientRes.data?.ssn_last4,
        dob: clientRes.data?.dob || 'EMPTY',
        date_of_birth: clientRes.data?.date_of_birth || 'EMPTY',
        has_dob: clientRes.data?.has_dob,
        monthly_income: clientRes.data?.monthly_income || 'EMPTY',
        income: clientRes.data?.income || 'EMPTY',
        employment: clientRes.data?.employment || 'EMPTY',
        employer_name: clientRes.data?.employer_name || 'EMPTY',
        residence_type: clientRes.data?.residence_type || 'EMPTY',
        loan_amount_requested: clientRes.data?.loan_amount_requested || 'EMPTY',
        deal_amount: clientRes.data?.deal_amount || 'EMPTY',
        loan_purpose: clientRes.data?.loan_purpose || 'EMPTY',
        banking_bank_name: clientRes.data?.banking_bank_name || 'EMPTY',
        banking_account_holder: clientRes.data?.banking_account_holder || 'EMPTY',
      }, null, 2));
      // v3.15-574 STABLE_MAPPING_V1: Frontend guard — detect invalid nested structures
      if (clientRes.data?.application || clientRes.data?.details || clientRes.data?.data) {
        console.error("INVALID DATA STRUCTURE: client object contains nested 'application', 'details', or 'data' key. This indicates a backend mapping regression.");
      }
      setClient(clientRes.data);
      setEditData(clientRes.data);
      
      // SECONDARY fetches - use allSettled so failures don't block page
      const secondaryPromises = [
        api.get(`/clients/${id}/activities`),
        api.get(`/clients/${id}/documents`),
        api.get('/users'),
        api.get(`/users/assignable-for-task/${id}`),
      ];
      
      // PTC users can fetch PTC reps, companies, and lenders for reassignment
      if (isInternal()) {
        secondaryPromises.push(api.get('/users/ptc-reps'));
        secondaryPromises.push(api.get('/companies/external'));
        secondaryPromises.push(api.get('/lenders'));
        secondaryPromises.push(api.get('/user/permissions'));
      }
      
      const results = await Promise.allSettled(secondaryPromises);
      
      // Handle each result individually - log errors but don't block
      // Index 0: activities
      if (results[0].status === 'fulfilled') {
        setActivities(results[0].value.data);
      } else {
        console.warn('Failed to fetch activities:', results[0].reason?.message);
        setActivities([]);
      }
      
      // Index 1: documents
      if (results[1].status === 'fulfilled') {
        setDocuments(results[1].value.data);
      } else {
        console.warn('Failed to fetch documents:', results[1].reason?.message);
        setDocuments([]);
      }
      
      // Index 2: users
      if (results[2].status === 'fulfilled') {
        setUsers(results[2].value.data);
      } else {
        console.warn('Failed to fetch users:', results[2].reason?.message);
        setUsers([]);
      }
      
      // Index 3: assignable users
      if (results[3].status === 'fulfilled') {
        setAssignableUsers(results[3].value.data);
      } else {
        console.warn('Failed to fetch assignable users:', results[3].reason?.message);
        setAssignableUsers([]);
      }
      
      // PTC-only secondary data (indices 4-7)
      if (isInternal()) {
        // Index 4: PTC reps
        if (results[4]?.status === 'fulfilled') {
          setPtcReps(results[4].value.data || []);
        } else {
          console.warn('Failed to fetch PTC reps:', results[4]?.reason?.message);
          setPtcReps([]);
        }
        
        // Index 5: companies
        if (results[5]?.status === 'fulfilled') {
          setCompanies(results[5].value.data || []);
        } else {
          console.warn('Failed to fetch companies:', results[5]?.reason?.message);
          setCompanies([]);
        }
        
        // Index 6: lenders
        if (results[6]?.status === 'fulfilled') {
          setLenders(results[6].value.data || []);
        } else {
          console.warn('Failed to fetch lenders:', results[6]?.reason?.message);
          setLenders([]);
        }
        
        // Index 7: user permissions
        if (results[7]?.status === 'fulfilled') {
          setUserPermissions(results[7].value.data || {});
        } else {
          console.warn('Failed to fetch user permissions:', results[7]?.reason?.message);
          setUserPermissions({});
        }
      }

      // Fetch tasks for this client (non-blocking)
      try {
        const tasksRes = await api.get('/tasks');
        setTasks(tasksRes.data.filter((t) => t.client_id === id));
      } catch (taskError) {
        console.warn('Failed to fetch tasks:', taskError);
        setTasks([]);
      }

      // v3.14-529: Check comms permission for external orgs
      if (!isInternal() && user?.company_id) {
        try {
          const companyRes = await api.get(`/companies/${user.company_id}`);
          setCommsEnabled(companyRes.data?.comms_enabled === true);
        } catch {
          setCommsEnabled(false);
        }
      } else if (isInternal()) {
        setCommsEnabled(true); // Internal users always have comms
      }
    } catch (error) {
      // v3.14-109: ONLY redirect if PRIMARY client fetch fails
      console.error('Failed to fetch client:', error);
      toast.error('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, isInternal]);

  // Fetch associated deal from ptc_deals collection
  const fetchDeal = useCallback(async (clientId) => {
    try {
      // Try to find deal by client_id
      const response = await api.get(`/ptc-deals/by-client/${clientId}`);
      if (response.data) {
        setDeal(response.data);
      }
    } catch (error) {
      // Deal might not exist yet, that's okay
      console.log('No deal found for client:', clientId);
      setDeal(null);
    }
  }, []);

  useEffect(() => {
    fetchClient();
    fetchFinancingOffers();
    if (id) {
      fetchDeal(id);
    }
  }, [fetchClient, id, fetchDeal]);

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        full_name: editData.full_name,
        phone: editData.phone,
        email: editData.email,
        // Address fields
        street_address: editData.street_address || null,
        city: editData.city || null,
        state: editData.state || null,
        zip_code: editData.zip_code || null,
        // Company assignment (Admin/Internal can change company)
        company_id: editData.company_id || client?.company_id,
        // Assignments - director and sales rep are text fields now
        deal_stage: editData.deal_stage,
        director_name: editData.director_name || null,
        sales_rep_name: editData.sales_rep_name || null,
        ptc_rep_id: editData.ptc_rep_id || null,
        requested_amount: editData.requested_amount,
        deal_amount: editData.deal_amount,
        approved_amount: editData.approved_amount,
        term: editData.term,
        payment: editData.payment,
        rate: editData.rate,
        funding_status: editData.funding_status,
        ach_entered: editData.ach_entered,
        ach_transaction_amount: editData.ach_transaction_amount,
        ach_eta: editData.ach_eta,
        funding_date: editData.funding_date,
        funded_amount: editData.funded_amount,
        // PTC Payment fields - v3.14-102: Removed ptc_payment_status (use settlement_status)
        ptc_fee_amount: editData.ptc_fee_amount,
        ptc_payment_due_date: editData.ptc_payment_due_date,
        ptc_payment_received_date: editData.ptc_payment_received_date,
        external_notes: editData.external_notes,
        is_declined: editData.is_declined,
        is_cancelled: editData.is_cancelled,
        did_not_finance: editData.did_not_finance,
        // Second Client / Co-Applicant
        full_name_2: editData.full_name_2 || null,
        email_2: editData.email_2 || null,
        phone_2: editData.phone_2 || null,
        // Confirmation Numbers
        credit_app_confirmation_number: editData.credit_app_confirmation_number || null,
        ach_confirmation_number: editData.ach_confirmation_number || null,
        // v3.14-151: New Transaction Identifiers
        ach_signature_confirmation: editData.ach_signature_confirmation || null,
        ach_bank_confirmation_number: editData.ach_bank_confirmation_number || null,
        wire_transfer_confirmation_number: editData.wire_transfer_confirmation_number || null,
        // v3.14-151: Payment Tracking Dates
        partner_paid_date: editData.partner_paid_date || null,
        ptc_paid_date: editData.ptc_paid_date || null,
      };

      // Add internal fields if PTC user
      if (isInternal()) {
        payload.lending_institution = editData.lending_institution || null;
        payload.lender_contact_name = editData.lender_contact_name || null;
        payload.lender_contact_phone = editData.lender_contact_phone || null;
        payload.lender_contact_email = editData.lender_contact_email || null;
        payload.internal_notes = editData.internal_notes || null;
        payload.internal_processing_notes = editData.internal_processing_notes || null;
        payload.commission_rate = editData.commission_rate || null;
        payload.compensation_notes = editData.compensation_notes || null;
        // Payroll fields
        payload.deal_source = editData.deal_source;
        payload.vendor_payment_received_date = editData.vendor_payment_received_date;
        // Role assignments for commissions
        payload.sales_rep_id = editData.sales_rep_id;
        payload.manager_id = editData.manager_id;
        payload.director_id = editData.director_id;
        payload.payment_method = editData.payment_method;
        // Include editable created_at for imports
        if (editData.created_at) {
          payload.created_at = editData.created_at;
        }
      }

      await api.put(`/clients/${id}`, payload);
      toast.success('Client updated');
      setIsEditOpen(false);
      fetchClient();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update client'));
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await api.put(`/clients/${id}`, { deal_stage: newStage });
      toast.success('Stage updated');
      fetchClient();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  // Confirm Funding Handler
  const handleConfirmFunding = async (e) => {
    e.preventDefault();
    setConfirmingFunding(true);
    
    try {
      const payload = {
        funding_date: confirmFundingData.funding_date,
        funded_amount: parseFloat(confirmFundingData.funded_amount) || client?.deal_amount || 0,
      };
      
      if (confirmFundingData.confirmation_number) {
        payload.confirmation_number = confirmFundingData.confirmation_number;
      }
      if (confirmFundingData.ach_confirmation_number) {
        payload.ach_confirmation_number = confirmFundingData.ach_confirmation_number;
      }
      
      await api.post(`/clients/${id}/confirm-funding`, payload);
      toast.success('Funding confirmed! Commissions have been triggered.');
      setConfirmFundingDialog(false);
      setConfirmFundingData({
        funding_date: new Date().toISOString().split('T')[0],
        funded_amount: '',
        confirmation_number: '',
        ach_confirmation_number: '',
      });
      fetchClient();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to confirm funding'));
    } finally {
      setConfirmingFunding(false);
    }
  };

  // Open Confirm Funding Dialog with pre-filled amount
  const openConfirmFundingDialog = () => {
    setConfirmFundingData({
      funding_date: new Date().toISOString().split('T')[0],
      funded_amount: client?.deal_amount || client?.approved_amount || '',
      confirmation_number: client?.credit_app_confirmation_number || '',
      ach_confirmation_number: client?.ach_confirmation_number || '',
    });
    setConfirmFundingDialog(true);
  };

  // v3.14-100: Bill.com functions REMOVED

  // v3.14-103: Stage-Driven Action Panel Handlers
  const handleStageAction = async (targetStage) => {
    setIsProcessingAction(true);
    try {
      let updatePayload = { deal_stage: targetStage };
      
      // For payment_processing → funded, validate and include payment data
      if (targetStage === 'funded') {
        if (!actionPanelData.amount_received || parseFloat(actionPanelData.amount_received) <= 0) {
          toast.error('Amount received is required');
          setIsProcessingAction(false);
          return;
        }
        if (!actionPanelData.payment_type) {
          toast.error('Payment type is required');
          setIsProcessingAction(false);
          return;
        }
        // ACH confirmation required for ACH-based payments
        if (['cash', 'external_finance', 'inhouse_finance'].includes(actionPanelData.payment_type)) {
          if (!actionPanelData.ach_confirmation_number?.trim()) {
            toast.error('ACH Confirmation Number is required');
            setIsProcessingAction(false);
            return;
          }
        }
        
        updatePayload = {
          ...updatePayload,
          ach_entered: true,
          ach_transaction_amount: parseFloat(actionPanelData.amount_received),
          payment_type: actionPanelData.payment_type,
          ach_confirmation_number: actionPanelData.ach_confirmation_number,
          vendor_payment_received_date: new Date().toISOString(),
          funded_amount: parseFloat(actionPanelData.amount_received),
        };
      }
      
      await api.put(`/clients/${id}`, updatePayload);
      
      const stageLabels = {
        'payment_processing': 'Sent to Payment Processing',
        'funded': 'Payment Confirmed - Deal Funded',
        'completed': 'Deal Completed'
      };
      
      toast.success(stageLabels[targetStage] || `Stage updated to ${targetStage}`);
      fetchClient();
      
      // Reset action panel data
      setActionPanelData({
        amount_received: '',
        payment_type: '',
        ach_confirmation_number: '',
      });
      
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update deal stage'));
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteContent.trim()) return;

    try {
      await api.post(`/clients/${id}/notes`, { 
        content: noteContent,
        is_internal: noteIsInternal
      });
      toast.success(noteIsInternal ? 'Internal note added' : 'Note added');
      setNoteContent('');
      setNoteIsInternal(false);
      setIsNoteOpen(false);
      fetchClient();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      // v3.15-1080: multi-assignee. If no users picked AND no external
      // email, default to creator so the task still has a reminder owner.
      const isExternal = newTask.external_email && newTask.assigned_to === 'external';
      const ids = (newTask.assigned_users || []).filter(Boolean);
      const payload = {
        title: newTask.title,
        description: newTask.description,
        client_id: id,
        due_date: newTask.due_date || null,
        due_time: newTask.due_time || null,
        external_email: isExternal ? newTask.external_email : null,
        assigned_users: ids.length ? ids : [user.id],
      };
      await api.post('/tasks', payload);
      toast.success(
        ids.length > 1
          ? `Task created — assigned to ${ids.length} users`
          : 'Task created',
      );
      setNewTask({ title: '', description: '', assigned_to: '', assigned_users: [], external_email: '', due_date: '', due_time: '' });
      setIsTaskOpen(false);
      fetchClient();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'completed' });
      toast.success('Task completed');
      fetchClient();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/clients/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      fetchClient();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  // v3.15-670: Package Tracking handlers
  const fetchTracking = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/tracking/deal/${id}`);
      if (res.data?.tracking) setTrackingData(res.data.tracking);
    } catch { /* no tracking yet */ }
  }, [id]);

  useEffect(() => { fetchTracking(); }, [fetchTracking]);

  // v3.15-711: Live presence — heartbeat every 12s, cleanup on leave
  useEffect(() => {
    if (!id) return;
    const sendHeartbeat = () => {
      api.post('/presence/heartbeat', { client_id: id }).catch(() => {});
      api.get(`/presence/${id}`).then(r => setViewers(r.data?.viewers || [])).catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 12000);
    return () => {
      clearInterval(interval);
      api.post('/presence/leave', { client_id: id }).catch(() => {});
    };
  }, [id]);

  const handleCreateTracker = async () => {
    if (!trackingInput.trim()) return;
    setCreatingTracker(true);
    try {
      const res = await api.post('/tracking/create', {
        deal_id: id,
        tracking_number: trackingInput.trim(),
        carrier: trackingCarrier || null,
      });
      setTrackingData(res.data);
      setTrackingInput('');
      setTrackingCarrier('');
      toast.success('Tracking created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tracker');
    } finally {
      setCreatingTracker(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted');
      navigate('/clients');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  // v3.14-401: Merge client
  const handleMergeSearch = async (query) => {
    setMergeSearch(query);
    if (query.length < 2) { setMergeResults([]); return; }
    try {
      const res = await api.get(`/clients?search=${encodeURIComponent(query)}&limit=10`);
      const results = (res.data?.clients || []).filter(c => c.id !== id && c.deal_stage !== 'merged');
      setMergeResults(results);
    } catch { setMergeResults([]); }
  };

  const handleMerge = async (secondaryId, secondaryName, resolution = null, ack = null) => {
    if (!resolution) {
      if (!window.confirm(`Merge "${secondaryName}" into "${client.full_name}"?\n\nThis will:\n- Copy their info as Client 2\n- Move all their deals, applications & communications here\n- Mark "${secondaryName}" as merged\n\nThis cannot be undone.`)) return;
    }
    setMerging(true);
    try {
      const body = { primary_client_id: id, secondary_client_id: secondaryId };
      if (resolution) body.resolution = resolution;
      if (ack) body.ack = ack;
      const res = await api.post('/clients/merge', body);
      toast.success(res.data?.message || 'Clients merged');
      setMergeOpen(false);
      setMergeSearch('');
      setMergeResults([]);
      // Refresh client data
      const updated = await api.get(`/clients/${id}`);
      setClient(updated.data);
    } catch (err) {
      // v3.21-1609: household conflict → prompt operator for resolution
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.error === 'household_conflict') {
        const plan = detail.plan || {};
        const options = detail.resolutions || [];
        const msg =
          `Household Matching Engine blocked this merge.\n\n` +
          `Reason: ${plan.reason}\n` +
          `Primary has Client 2: ${plan.primary_has_c2 ? 'YES' : 'no'}\n` +
          `Secondary has Client 2: ${plan.secondary_has_c2 ? 'YES' : 'no'}\n` +
          `Suggested C2 outcome: ${plan.c2_result}\n\n` +
          `Choose a resolution:\n` +
          options.map((o, i) => `  ${i + 1}. ${o}`).join('\n') +
          `\n\nEnter number (or Cancel to abort):`;
        const pick = window.prompt(msg, '1');
        if (!pick) { setMerging(false); return; }
        const idx = parseInt(pick, 10) - 1;
        const chosen = options[idx];
        if (!chosen) { toast.error('Invalid resolution choice'); setMerging(false); return; }
        let ackVal = null;
        if (chosen === 'force_overwrite') {
          ackVal = window.prompt('Type "OVERWRITE" to confirm destructive overwrite:', '');
          if (ackVal !== 'OVERWRITE') { toast.error('Overwrite not confirmed'); setMerging(false); return; }
        }
        setMerging(false);
        return handleMerge(secondaryId, secondaryName, chosen, ackVal);
      }
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Merge failed');
    }
    setMerging(false);
  };


  // Create Deal from client profile
  const handleCreateDeal = async () => {
    if (!newDealResorts[0]?.name) {
      toast.error('Please add at least one resort');
      return;
    }
    
    setCreatingDeal(true);
    try {
      const dealData = {
        client_id: id, // Link to this client
        resorts: newDealResorts.filter(r => r.name).map(r => ({
          name: r.name,
          price: parseFloat(r.price) || 0,
        })),
        notes: newDealNotes,
      };
      
      const response = await api.post('/deal-automation/deals', dealData);
      toast.success('Deal created successfully');
      
      // Save commission role assignments to the client
      const commissionUpdates = {};
      if (newDealCommission.sales_rep_id) commissionUpdates.sales_rep_id = newDealCommission.sales_rep_id;
      if (newDealCommission.manager_id) commissionUpdates.manager_id = newDealCommission.manager_id;
      if (newDealCommission.director_id) commissionUpdates.director_id = newDealCommission.director_id;
      if (newDealCommission.payment_method) commissionUpdates.payment_method = newDealCommission.payment_method;
      if (Object.keys(commissionUpdates).length > 0) {
        try {
          await api.put(`/clients/${id}`, commissionUpdates);
        } catch (e) {
          console.error('Failed to save commission roles:', e);
        }
      }
      
      // Auto-generate proposal
      if (response.data?.id) {
        try {
          await api.post(`/deal-automation/deals/${response.data.id}/proposal`);
          toast.success('Proposal generated - approval required to send');
        } catch (e) {
          console.error('Failed to generate proposal:', e);
        }
      }
      
      setShowCreateDealDialog(false);
      setNewDealResorts([{ name: '', price: '' }]);
      setNewDealNotes('');
      setNewDealCommission({ sales_rep_id: null, manager_id: null, director_id: null, payment_method: null });
      fetchClient(); // Refresh client data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    } finally {
      setCreatingDeal(false);
    }
  };

  // Get users for the client's company (for assignment dropdowns)
  const companyUsers = users.filter((u) => u.company_id === (editData.company_id || client?.company_id));
  const canDelete = isInternal() && (isAdmin() || isManager());

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <div className="space-y-0" data-testid="client-detail-page">
      {/* v3.17-1241 (OPS-002 #3) — Return-to-previous-location trail. */}
      <ReturnToTrail />
      {/* v3.18-1337 LAC-001 — Live Activity Claim strip.  Surfaces
          every workflow another operator is currently performing on
          THIS client so the current user has full situational
          awareness (no need to open the specific workspace to find
          out someone else is inside it). */}
      <div className="px-4 pt-3">
        <ClientLiveActivityStrip
          clientId={id}
          currentUserId={user?.user_id || user?.id}
          onCount={setActiveWorkflowClaimCount}
        />
      </div>
      {/* v3.15-1141: Ghost Contact Awareness — non-dismissible, blocks UI until acknowledged. */}
      {!ghostAcknowledged && (
        <GhostContactAwarenessModal
          clientId={id}
          onAcknowledge={() => setGhostAcknowledged(true)}
        />
      )}
      {/* v3.18-1250 CALL-TRACK-002 — after Ghost ack, force the rep to
          wrap up any pending inbound-call disposition for THIS client
          via the unified Activity Completion Framework. One dialog for
          every completable activity in the Hub. Non-dismissible. */}
      {ghostAcknowledged && pendingInboundDisposition && (
        <ActivityCompletionDialog
          open={true}
          onOpenChange={() => { /* non-dismissible gate — v3.15-1150 behaviour retained */ }}
          ruleEndpoint="/completion-rules/phone_call"
          itemTitle={`Incoming Call from ${pendingInboundDisposition.from_number || 'unknown caller'}`}
          itemSubtitle={
            pendingInboundDisposition.duration_sec
              ? `Duration ${Math.floor(pendingInboundDisposition.duration_sec / 60)}m ${pendingInboundDisposition.duration_sec % 60}s`
              : 'Document the outcome and notes to close this call.'
          }
          submitLabel="Complete Call"
          testIdPrefix="inbound-call-completion"
          onSubmit={async ({ outcome, notes }) => {
            await api.post(
              `/inbound-calls/pending-dispositions/${pendingInboundDisposition.id}/complete`,
              { disposition: outcome, notes },
            );
            setPendingInboundDisposition(null);
            refetchPendingInbound();
          }}
        />
      )}
      {/* v3.15-739: Deal Ownership gate modal — PTC only */}
      <Dialog open={ownershipGateOpen} onOpenChange={setOwnershipGateOpen}>
        <DialogContent data-testid="deal-ownership-required-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-amber-700">Deal Ownership Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-700">
              Before making any changes, you must complete the Deal Ownership section.
            </p>
            <p className="text-sm text-slate-700">
              This ensures proper tracking and accountability for all deals.
            </p>
            {ownershipMissing.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">Missing fields:</p>
                <ul className="list-disc pl-5 text-xs text-amber-900">
                  {ownershipMissing.map((f) => (<li key={f}>{f}</li>))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              data-testid="deal-ownership-goto-btn"
              onClick={() => {
                setOwnershipGateOpen(false);
                const el = document.querySelector('[data-testid="deal-ownership-right-block"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Go to Deal Ownership
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* v3.15-927: Schedule Appointment modal */}
      <ScheduleAppointmentModal
        open={showScheduleModal}
        onOpenChange={setShowScheduleModal}
        client={client}
        currentUser={user}
        onScheduled={() => { if (typeof fetchClient === 'function') fetchClient(); }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clients')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-primary">{client.full_name}</h1>
            <p className="text-sm text-slate-500">{client.company_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* v3.21-1622 · Enter the STANDARD Client Workspace from the
              individual client screen (replaces the removed Clients-list
              chip).  Opens the authoritative ClientWorkspaceModal from
              v3.21-1620 via the global provider — no duplicate impl. */}
          {isInternal() && client && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              onClick={() => {
                try {
                  window.dispatchEvent(new CustomEvent('client-workspace:open', {
                    detail: { clientId: client.id },
                  }));
                } catch { /* noop */ }
              }}
              data-testid="client-detail-open-workspace-btn"
              title="Open Client Workspace"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Client Workspace
            </Button>
          )}
          {/* v3.21-1625 · CLIENT-DETAIL-CLEANUP — Removed the following
              redundant toolbar entry points.  Their underlying services
              remain intact and reachable from their authoritative homes:
                • Schedule                → Client Workspace → Create Appointment
                • Copy Booking Link       → Settings → Availability + public /book/*
                • Confirm Vendor Payment  → Money Manager Admin surface only
                • Create Deal             → New Deal button on Clients / Pipeline
              Rationale: SSOT — Deal Paid in Full — All Parties Settled is the
              sole financial completion control on this screen; scheduling and
              deal creation belong to the Workspace and List pages respectively. */}
          {isInternal() && client && (
            <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer ${
              client.paid_in_full ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-300 hover:bg-slate-50'
            }`} data-testid="paid-in-full-toggle">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={!!client.paid_in_full}
                disabled={!!client.paid_in_full}
                onChange={(e) => {
                  // v3.15-981: NEVER auto-send. Always open the confirmation
                  // modal so a human reviews recipients + body before SMTP.
                  if (!e.target.checked || client.paid_in_full) return;
                  setPaidInFullConfirmOpen(true);
                }}
                data-testid="paid-in-full-checkbox"
              />
              <span>{client.paid_in_full ? 'Deal Paid in Full ✓' : 'Deal Paid in Full – All Parties Settled'}</span>
            </label>
          )}
          {/* v3.15-1073: 💰 Send to Money Manager — PTC ADMIN ROLE ONLY.
              Hidden entirely for everyone else (no button, no error).
              Idempotent: clicking when a row already exists just navigates
              to the ledger. NOT connected to SSOT/payroll/commissions/funding. */}
          {isAdmin() && client && (
            <Button
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/money-manager/send-from-client/${client.id}`);
                  if (data?.created) {
                    toast.success('Sent to Money Manager');
                  } else {
                    toast.info('Already in Money Manager — opening ledger');
                  }
                  navigate('/money-manager');
                } catch (e) {
                  toast.error(getApiErrorMessage(e) || 'Send to Money Manager failed');
                }
              }}
              data-testid="send-to-money-manager-btn"
              title="Create a Money Manager ledger row for this client. PTC Admin only."
            >
              <Banknote className="h-4 w-4 mr-2" />
              💰 Send to Money Manager
            </Button>
          )}
          {/* v3.21-1625 · Confirm Vendor Payment button removed from this
              toolbar — SSOT is "Deal Paid in Full — All Parties Settled".
              The endpoint still exists on the backend for the Money
              Manager admin surface (see openConfirmFundingDialog handler
              which remains in this file for the modal that other flows
              open).  Only the redundant Client Detail entry point is
              gone. */}
          {/* v3.21-1625 · Create Deal button removed from the Client
              Detail toolbar — the user is already inside an existing
              client/deal, so a Create Deal entry point here encourages
              duplicates.  New Deal remains reachable from the Clients
              list and the Pipeline page. */}
          {/* v3.14-236: Edit button removed — all fields now inline editable */}

          {/* v3.14-401: Merge Client */}
          {canDelete && (
            <>
              <Button variant="outline" onClick={() => setMergeOpen(!mergeOpen)} data-testid="merge-client-btn" className="relative">
                <Merge className="h-4 w-4 mr-1" /> Merge
              </Button>
              {mergeOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white border rounded-xl shadow-xl z-50 p-3" data-testid="merge-dialog">
                  <p className="text-xs text-slate-500 mb-2 font-medium">Search for client to merge into this one:</p>
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={mergeSearch}
                      onChange={(e) => handleMergeSearch(e.target.value)}
                      placeholder="Search by name, email, phone..."
                      className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      data-testid="merge-search-input"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {mergeResults.length === 0 && mergeSearch.length >= 2 && (
                      <p className="text-xs text-slate-400 text-center py-3">No clients found</p>
                    )}
                    {mergeResults.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleMerge(c.id, c.full_name)}
                        disabled={merging}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-lg text-sm transition-colors flex justify-between items-center group"
                        data-testid={`merge-option-${c.id}`}
                      >
                        <div>
                          <p className="font-medium text-slate-800">{c.full_name || 'No Name'}</p>
                          <p className="text-xs text-slate-500">{c.email || c.phone || 'No contact'}</p>
                        </div>
                        <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 font-medium">Merge</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => { setMergeOpen(false); setMergeSearch(''); setMergeResults([]); }} className="mt-2 w-full text-xs text-slate-400 hover:text-slate-600 text-center">
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}

          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 hover:text-red-700" data-testid="delete-client-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Client</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this client? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteClient} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>


      {/* v3.15-711: Live presence banner */}
      {/* v3.18-1348 LAC-FIX — the generic presence pill only renders
          when there is NO active workflow claim. Workflow claims
          carry far more operational value ("Al is entering a credit
          application") than a plain "Also viewing" indicator, so we
          defer to them when both exist. */}
      {/* v3.21-1621 — clicking the presence banner opens the
          internal Team Chat window for this client, so reps who are
          both viewing can start a quick conversation without leaving
          the client screen. */}
      {viewers.length > 0 && activeWorkflowClaimCount === 0 && (
        <button
          type="button"
          onClick={() => {
            try {
              window.dispatchEvent(new CustomEvent('team-chat:open', {
                detail: { clientId: id, clientName: client?.full_name },
              }));
            } catch { /* noop */ }
          }}
          className="mx-1 px-3 py-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-400 rounded-lg flex items-center gap-2 w-full text-left transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500"
          data-testid="presence-banner"
          title="Open internal Team Chat with these employees"
        >
          <div className="flex -space-x-1.5">
            {viewers.slice(0, 3).map((v) => (
              <div key={v.user_id} className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold border-2 border-white">
                {v.name?.charAt(0) || '?'}
              </div>
            ))}
          </div>
          <span className="text-xs text-amber-800 flex-1">
            Also viewing: <b>{viewers.slice(0, 2).map(v => v.name?.split(' ')[0]).join(', ')}</b>
            {viewers.length > 2 && <span className="text-amber-700"> + {viewers.length - 2} other{viewers.length - 2 === 1 ? '' : 's'}</span>}
          </span>
          <span className="text-[10px] font-semibold text-amber-800 bg-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1"
                data-testid="presence-banner-chat-hint">
            <MessageCircle className="h-3 w-3" />
            Team Chat
          </span>
        </button>
      )}

      {/* v3.15-976 PART 1: Blocking "Outcome Required" modal REMOVED.
          Email follow-up (10AM next-day client TZ) is now the sole nudge.
          The non-blocking banner is also retired per spec — reps should
          NEVER be interrupted by offer-age UI on the deal page. */}

      {/* v3.15-977: Bill.com Customer Sync badge + Retry button (admin/internal). */}
      {client?.id && (
        <div className="px-1 -mt-1 mb-1" data-testid="bill-sync-row">
          <BillSyncBadge clientId={client.id} />
        </div>
      )}

      {/* v3.14-165: Unified View - DealDashboard (TOP GRID, SECOND GRID, STAGE STRIP, FINANCE OPTIONS) */}
      <DealDashboard
          client={client}
          user={user}
          isInternal={isInternal()}
          canEditFundingInfo={canEditFundingInfo}
          canEditPtcPayment={canEditPtcPayment}
          companies={companies}
          ptcReps={ptcReps}
          users={users}
          fetchClient={fetchClient}
          onEdit={() => {}}
          onSave={async () => {
            fetchClient();
          }}
          onMarkPaid={() => {
            toast.info('Use the Edit dialog to mark as paid');
          }}
          onCancelDeal={() => {
            toast.info('Use the Edit dialog to cancel deal');
          }}
          onSetNextAction={async (nextAction) => {
            try {
              await api.put(`/clients/${id}`, { next_action: nextAction });
              toast.success('Next action updated');
              fetchClient();
            } catch (error) {
              toast.error('Failed to update next action');
            }
          }}
          onCreateInvoice={async () => {
            try {
              const res = await api.post(`/billcom/send-invoice/${id}`, {});
              if (res.data?.status === 'already_sent') {
                toast.info('Invoice already sent to Bill.com');
              } else {
                toast.success(`Invoice sent — ${res.data?.billcom_invoice_number || ''}`);
              }
              fetchClient();
            } catch (error) {
              toast.error(error?.response?.data?.detail || 'Failed to create invoice');
            }
          }}
          onUpdateField={async (fieldName, value) => {
            try {
              const body = typeof fieldName === 'object' ? fieldName : { [fieldName]: value };
              // v3.15-1068 (Tier 3) — inline edits to applied_for_amount /
              // deal_amount / approved_amount must pass through the financial
              // confirmation gate before hitting PUT. If the operator cancels,
              // we abort silently (the field's local edit state is whatever
              // the EditableField component handles).
              const gate = confirmFinancialChanges(client, body);
              if (!gate.ok) {
                toast.info('Change cancelled — financial value preserved.');
                return;
              }
              console.log("SAVE PAYLOAD:", JSON.stringify(gate.payload));
              const putResp = await api.put(`/clients/${id}`, gate.payload);
              console.log("SAVE RESPONSE banking_bank_name:", putResp.data?.banking_bank_name, "street_address:", putResp.data?.street_address);
              toast.success('Field updated');
              // v3.21-1805 · Hydrate Client state from the authoritative
              // PUT response BEFORE issuing any follow-on refetch.  The
              // backend returns the persisted document, so the just-saved
              // `current_finturf_application_id` (or any other field) is
              // immediately reflected in the UI.  We also increment the
              // fetchClient generation so any stale in-flight GET whose
              // response has not yet landed cannot clobber this snapshot.
              if (putResp && putResp.data) {
                fetchClientGenRef.current += 1;
                setClient(putResp.data);
                setEditData(putResp.data);
              }
              fetchClient();
            } catch (error) {
              if (handleOwnershipGateError(error)) return;
              const label = typeof fieldName === 'object' ? Object.keys(fieldName).join(', ') : fieldName;
              console.error('Field update error:', label, error?.response?.data);
              // v3.15-973 PART 3 — When the dead-deal gate fires (409 with
              // structured detail), refetch so the gate modal opens. Always
              // pass through getApiErrorMessage to avoid rendering objects.
              if (error?.response?.status === 409 && error?.response?.data?.detail?.error === 'must_select_outcome') {
                fetchClient();
                return; // modal will open on its own
              }
              toast.error(getApiErrorMessage(error, `Failed to update ${label}`));
            }
          }}
          saving={saving}
        />

      {/* PTC Deal Progress removed */}

      {/* v3.14-228: Finance Offers — HIDDEN (no lender access) */}

      {/* PTC-Only sections moved below Activity tabs */}

      {/* v3.15-568: DealFinancialPanel removed — user requested removal */}

      {/* v3.15: Sensitive Data card removed — SSN/DOB visible directly in Client Info for internal PTC users */}

      {/* Profit Calculator removed — PTCDealFinancials already shows profit data */}



      {/* v3.14-175: Credit Application horizontal, Finance Offers, and PTC sections above */}

      {/* Add Credit Application Dialog */}
      <Dialog open={isAddCreditAppOpen} onOpenChange={setIsAddCreditAppOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-credit-app-dialog">
          <DialogHeader>
            <DialogTitle>Credit Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Personal Information */}
            <div className="border-b pb-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Personal Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={creditAppData.full_name}
                    onChange={(e) => setCreditAppData({ ...creditAppData, full_name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="ca-form-name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={creditAppData.email}
                    onChange={(e) => setCreditAppData({ ...creditAppData, email: e.target.value })}
                    placeholder="john@example.com"
                    data-testid="ca-form-email"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={creditAppData.phone}
                    onChange={(e) => setCreditAppData({ ...creditAppData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="ca-form-phone"
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={creditAppData.date_of_birth}
                    onChange={(e) => setCreditAppData({ ...creditAppData, date_of_birth: e.target.value })}
                    data-testid="ca-form-dob"
                  />
                </div>
                <div>
                  <Label>SSN (Full 9 digits)</Label>
                  <Input
                    value={creditAppData.ssn}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setCreditAppData({ ...creditAppData, ssn: val });
                    }}
                    placeholder="123456789"
                    maxLength={9}
                    data-testid="ca-form-ssn"
                  />
                  <p className="text-xs text-slate-500 mt-1">Required for credit processing</p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="border-b pb-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Address</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Street Address</Label>
                  <Input
                    value={creditAppData.street_address}
                    onChange={(e) => setCreditAppData({ ...creditAppData, street_address: e.target.value })}
                    placeholder="123 Main St"
                    data-testid="ca-form-street"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={creditAppData.city}
                    onChange={(e) => setCreditAppData({ ...creditAppData, city: e.target.value })}
                    placeholder="Miami"
                    data-testid="ca-form-city"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={creditAppData.state}
                    onChange={(e) => setCreditAppData({ ...creditAppData, state: e.target.value })}
                    placeholder="FL"
                    data-testid="ca-form-state"
                  />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input
                    value={creditAppData.zip_code}
                    onChange={(e) => setCreditAppData({ ...creditAppData, zip_code: e.target.value })}
                    placeholder="33101"
                    data-testid="ca-form-zip"
                  />
                </div>
              </div>
            </div>

            {/* Employment & Financial */}
            <div className="border-b pb-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Employment & Financial</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monthly Income</Label>
                  <Input
                    type="number"
                    value={creditAppData.monthly_income}
                    onChange={(e) => setCreditAppData({ ...creditAppData, monthly_income: e.target.value })}
                    placeholder="5000"
                    data-testid="ca-form-income"
                  />
                </div>
                <div>
                  <Label>Employment Status</Label>
                  <Select 
                    value={creditAppData.employment_length || "none"} 
                    onValueChange={(value) => setCreditAppData({ ...creditAppData, employment_length: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="ca-form-employment">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      <SelectItem value="less_than_1_year">Less than 1 year</SelectItem>
                      <SelectItem value="1_3_years">1-3 years</SelectItem>
                      <SelectItem value="3_5_years">3-5 years</SelectItem>
                      <SelectItem value="5_plus_years">5+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Residence</Label>
                  <Select 
                    value={creditAppData.residence_type || "none"} 
                    onValueChange={(value) => setCreditAppData({ ...creditAppData, residence_type: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="ca-form-residence">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      <SelectItem value="own">Own</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Loan Request */}
            <div className="border-b pb-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Loan Request</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Loan Amount Requested</Label>
                  <Input
                    type="number"
                    value={creditAppData.loan_amount_requested}
                    onChange={(e) => setCreditAppData({ ...creditAppData, loan_amount_requested: e.target.value })}
                    placeholder="25000"
                    data-testid="ca-form-loan-amount"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Loan Purpose</Label>
                  <Input
                    value={creditAppData.loan_purpose}
                    onChange={(e) => setCreditAppData({ ...creditAppData, loan_purpose: e.target.value })}
                    placeholder="Debt consolidation, home improvement, etc."
                    data-testid="ca-form-loan-purpose"
                  />
                </div>
              </div>
            </div>

            {/* Rep Information */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Originating Rep</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rep Name</Label>
                  <Input
                    value={creditAppData.source_rep_name}
                    onChange={(e) => setCreditAppData({ ...creditAppData, source_rep_name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="ca-form-rep-name"
                  />
                </div>
                <div>
                  <Label>Rep Email</Label>
                  <Input
                    type="email"
                    value={creditAppData.source_rep_email}
                    onChange={(e) => setCreditAppData({ ...creditAppData, source_rep_email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="ca-form-rep-email"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddCreditAppOpen(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSaveCreditApp}
              disabled={savingCreditApp}
              data-testid="save-credit-app-btn"
            >
              {savingCreditApp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Credit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* v3.14-175: Old lower grid REMOVED — all sections now above in compact layout */}




      {/* v3.14-179: UNIFIED — Communications, Activity, Tasks, Docs + Action Buttons */}
      <Card className="w-full" data-testid="unified-comms-section">
        <Tabs value={activeBottomTab} onValueChange={onTabChange} className="w-full">
          <CardHeader className="pb-0 pt-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <TabsList className="justify-start">
                <TabsTrigger value="activity" data-testid="activity-tab" className={blinkCls('activity')} data-blink={tabAttention?.activity?.blink ? 'true' : 'false'}>Activity{tabAttention?.activity?.unseen ? <span className="ml-1 text-[10px]">({tabAttention.activity.unseen})</span> : null}</TabsTrigger>
                <TabsTrigger value="appointment" data-testid="appointment-tab" className={blinkCls('appointment')} data-blink={tabAttention?.appointment?.blink ? 'true' : 'false'}>
                  Appointment{client?.appointment_event_id ? ' *' : ''}{tabAttention?.appointment?.unseen ? <span className="ml-1 text-[10px]">({tabAttention.appointment.unseen})</span> : null}
                </TabsTrigger>
                <TabsTrigger value="finance-history" data-testid="finance-history-tab" className={blinkCls('finance-history')} data-blink={tabAttention?.['finance-history']?.blink ? 'true' : 'false'}>Finance History{tabAttention?.['finance-history']?.unseen ? <span className="ml-1 text-[10px]">({tabAttention['finance-history'].unseen})</span> : null}</TabsTrigger>
                <TabsTrigger value="notes" data-testid="notes-tab" className={blinkCls('notes')} data-blink={tabAttention?.notes?.blink ? 'true' : 'false'}>
                  Notes{tabAttention?.notes?.unseen ? <span className="ml-1 text-[10px]">({tabAttention.notes.unseen})</span> : null}{(() => {
                    // v3.15-1129 — pinned count now reflects ANY pinned activity
                    // (not just notes) per the new spec.
                    const pinnedCount = activities.filter(a => a.is_pinned).length;
                    if (pinnedCount === 0) return null;
                    return (
                      <span
                        className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-cyan-700"
                        data-testid="notes-tab-pinned-count"
                        title={`${pinnedCount} pinned item${pinnedCount === 1 ? '' : 's'}`}
                      >
                        <Pin className="h-2.5 w-2.5" /> {pinnedCount}
                      </span>
                    );
                  })()}
                </TabsTrigger>
                <TabsTrigger value="communications" data-testid="communications-tab" className={blinkCls('communications')} data-blink={tabAttention?.communications?.blink ? 'true' : 'false'}>
                  {/* v3.21-1813 · Rename "Communications" → "Communication History".
                      Show canonical total_count and per-user NEW badge
                      (text label — never color alone, per accessibility). */}
                  <span className="hidden sm:inline">Communication History</span>
                  <span className="sm:hidden">Comm. History</span>
                  {commSummary?.total_count > 0 && (
                    <span className="ml-1 text-[10px]" data-testid="communications-tab-total">({commSummary.total_count})</span>
                  )}
                  {commSummary?.unread_count > 0 && (
                    <span
                      className="ml-1.5 inline-flex items-center rounded-full bg-red-600 text-white text-[9px] font-semibold px-1.5 py-0.5 leading-none"
                      data-testid="communications-tab-new-badge"
                      aria-label={`${commSummary.unread_count} new communication${commSummary.unread_count === 1 ? '' : 's'}`}
                    >
                      {commSummary.unread_count} NEW
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="tasks" data-testid="tasks-tab" className={blinkCls('tasks')} data-blink={tabAttention?.tasks?.blink ? 'true' : 'false'}>Tasks ({tasks.length}){tabAttention?.tasks?.unseen ? <span className="ml-1 text-[10px]">•</span> : null}</TabsTrigger>
                <TabsTrigger value="documents" data-testid="documents-tab" className={blinkCls('documents')} data-blink={tabAttention?.documents?.blink ? 'true' : 'false'}>Documents ({documents.length}){tabAttention?.documents?.unseen ? <span className="ml-1 text-[10px]">•</span> : null}</TabsTrigger>
                <TabsTrigger value="tracking" data-testid="tracking-tab" className={blinkCls('tracking')} data-blink={tabAttention?.tracking?.blink ? 'true' : 'false'}>
                  <Package className="h-3 w-3 mr-1" />Tracking{trackingData ? ` (${trackingData.status_label || trackingData.status})` : ''}{tabAttention?.tracking?.unseen ? <span className="ml-1 text-[10px]">•</span> : null}
                </TabsTrigger>
                {canSeeOwnershipTab && (
                  <TabsTrigger value="ownership" data-testid="ownership-tab-trigger" className={blinkCls('ownership')} data-blink={tabAttention?.ownership?.blink ? 'true' : 'false'}>
                    <Home className="h-3 w-3 mr-1" />Ownership Information{tabAttention?.ownership?.unseen ? <span className="ml-1 text-[10px]">•</span> : null}
                  </TabsTrigger>
                )}
                {/* v3.15-1230 SMART-INTAKE-003 — Payment Methods surface. */}
                <TabsTrigger value="payment-methods" data-testid="payment-methods-tab-trigger" className={blinkCls('payment-methods')} data-blink={tabAttention?.['payment-methods']?.blink ? 'true' : 'false'}>
                  <ShieldCheck className="h-3 w-3 mr-1" />Payment Methods{tabAttention?.['payment-methods']?.unseen ? <span className="ml-1 text-[10px]">•</span> : null}
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2">
                {/* v3.14-529: Email/SMS/Call only for internal users or external orgs with comms_enabled */}
                {commsEnabled && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                      onClick={() => {
                        // v3.21-1641 · Send Email from Client Detail
                        // dispatches the global compose event with the
                        // authoritative client email so the shared
                        // ComposeAndApprove opens with To pre-populated.
                        // Communications tab activation is preserved
                        // for reps who want the audit thread visible
                        // while composing.
                        try {
                          const evt = new CustomEvent('hub:open-compose', {
                            detail: {
                              to: (client?.email || '').trim(),
                              client_id: client?.id || null,
                            },
                          });
                          window.dispatchEvent(evt);
                        } catch (_e) { /* noop */ }
                        setActiveBottomTab('communications');
                      }}
                      data-testid="send-email-action-btn"
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Send Email
                    </Button>
                    {/* v3.18-1377 GMAIL-002 Phase 1 — Send SMS, Credit App
                        SMS, and ACH SMS buttons are physically removed from
                        the rendered DOM (not CSS-hidden) until SMS is
                        fully implemented. Backend handlers stay intact so
                        a future sprint can restore the surface. See the
                        deleted-block audit in /app/memory/PRD.md. */}
                    {client?.phone && (
                      <div className="flex items-center gap-1">
                        <Select value={callAs} onValueChange={setCallAs}>
                          <SelectTrigger className="h-7 text-[10px] w-[90px] border-orange-200" data-testid="call-as-client">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="eqf_hub">EQF / PTC Hub</SelectItem>
                            <SelectItem value="ptc_travel">PTC Travel</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs text-orange-700 border-orange-300 hover:bg-orange-50"
                          onClick={handleClickToCall}
                          disabled={calling}
                          data-testid="click-to-call-btn"
                        >
                          {calling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <PhoneOutgoing className="h-3 w-3 mr-1" />}
                          {calling ? 'Calling...' : 'Call'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                  onClick={() => setIsTaskOpen(true)}
                  data-testid="create-task-action-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Create Task
                </Button>
                {/* v3.21-1746 · Operational reaction buttons — moved
                    INTO this Activity toolbar directly after Create
                    Task, with an intentional visual gap.  Each button
                    dispatches a `hub:deal-action` window event that
                    DealDashboard.jsx handles — reusing every existing
                    handler, DraftEmailModal, RallyModal, CtcModal,
                    permission gate, and Activity write.  Date chip
                    beside each button reads the canonical `activities`
                    Activity SSOT via /api/clients/{id}/action-last-dates. */}
                {typeof isInternal === 'function' && isInternal() && (
                  <div
                    className="flex flex-wrap items-center gap-1.5 ml-2 pl-2 border-l border-slate-200"
                    data-testid="activity-toolbar-operational-group"
                  >
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`h-7 text-xs ${client?.at_risk_manual ? 'text-slate-700 border-slate-300 hover:bg-slate-50' : 'text-red-700 border-red-300 hover:bg-red-50'}`}
                        onClick={() => dispatchDealAction('at-risk')}
                        data-testid="toolbar-at-risk-btn"
                      >
                        {client?.at_risk_manual ? 'Clear At Risk' : 'Mark as At Risk'}
                      </Button>
                      <span className="text-[10px] text-slate-500" data-testid="toolbar-at-risk-date">
                        {fmtDateChip(actionLastDates?.at_risk)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`h-7 text-xs ${client?.is_hard_to_reach ? 'text-slate-700 border-slate-300 hover:bg-slate-50' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                        onClick={() => dispatchDealAction('hard-to-reach')}
                        data-testid="toolbar-hard-to-reach-btn"
                      >
                        {client?.is_hard_to_reach ? 'Clear HTR' : 'Mark Hard to Reach'}
                      </Button>
                      <span className="text-[10px] text-slate-500" data-testid="toolbar-hard-to-reach-date">
                        {fmtDateChip(actionLastDates?.hard_to_reach)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                        onClick={() => dispatchDealAction('rally')}
                        data-testid="toolbar-lets-get-this-deal-btn"
                      >
                        Let's Get This Deal
                      </Button>
                      <span className="text-[10px] text-slate-500" data-testid="toolbar-rally-date">
                        {fmtDateChip(actionLastDates?.rally || client?.last_rallied_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                        onClick={() => dispatchDealAction('clear-to-collect')}
                        data-testid="toolbar-clear-to-collect-btn"
                      >
                        Clear To Collect
                      </Button>
                      <span className="text-[10px] text-slate-500" data-testid="toolbar-clear-to-collect-date">
                        {fmtDateChip(actionLastDates?.clear_to_collect)}
                      </span>
                    </div>
                    {client?.original_sales_rep_id && client?.sales_rep_id !== client?.original_sales_rep_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                        onClick={() => dispatchDealAction('return-to-rep')}
                        data-testid="toolbar-return-original-rep-btn"
                      >
                        Return to Original Rep
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-3 px-4 pb-4">
            {/* Activity Tab — excludes finance-related entries */}
            <TabsContent value="activity" className="mt-0">
              {/* v3.21-1813 · Last Communication summary strip.  Renders
                  the freshest canonical communication so operators
                  immediately know if another employee just spoke,
                  texted, or emailed the Client — WITHOUT opening the
                  Communication History tab first. */}
              <LastCommunicationStrip
                summary={commSummary}
                onOpenHistory={openCommunicationHistory}
              />
              {/* v3.15-1126 — Pinned Notes section ALSO renders above the
                  activity timeline (per v3.15-1124 spec: "Pinned notes
                  always appear above the activity timeline"). Pin / Edit
                  / Delete controls are now also exposed on note-type rows
                  inside the activity timeline itself. */}
              {(() => {
                // v3.15-1129 — Pinned items now include ANY pinned activity
                // (notes, stage changes, calls, emails, etc.) per the new spec.
                const pinned = activities.filter(a => a.is_pinned);
                if (pinned.length === 0) return null;
                return (
                  <div className="mb-3" data-testid="activity-pinned-notes-section">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Pin className="h-3 w-3 text-cyan-700" />
                      <Label className="text-[10px] text-cyan-700 uppercase tracking-wide font-semibold">Pinned</Label>
                      <span className="text-[10px] text-slate-400">· {pinned.length}</span>
                    </div>
                    <div className="space-y-2">
                      {pinned.map(note => {
                        const noteTypes2 = ['note_added', 'internal_note_added', 'note'];
                        const isNoteP = noteTypes2.includes(note.activity_type) || note.type === 'note';
                        const modifiable = isNoteP && canModifyNote(note);
                        const isEditing = editingNoteId === note.id;
                        // v3.21-1667 · attention_color visual treatment (isolated — never touches Deal fields)
                        const att = isNoteP ? getAttentionStyle(note.attention_color) : null;
                        const baseBg = note.is_internal ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100';
                        const wrapBg = att ? `${att.tint} border-slate-100 ${att.accent}` : baseBg;
                        return (
                          <div
                            key={`pin-${note.id}`}
                            className={`flex items-start gap-3 py-2.5 px-3 rounded-lg border ring-1 ring-cyan-200 ${wrapBg}`}
                            data-testid={`activity-pinned-note-${note.id}`}
                            data-attention-color={att ? att.color : 'neutral'}
                          >
                            <div className={`mt-0.5 p-1.5 rounded ${note.is_internal ? 'bg-amber-200' : 'bg-blue-100'}`}>
                              <FileText className="h-3.5 w-3.5 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              {isEditing ? (
                                <div className="space-y-2" data-testid={`activity-note-edit-${note.id}`}>
                                  <Textarea
                                    value={editingNoteContent}
                                    onChange={(e) => setEditingNoteContent(e.target.value)}
                                    rows={3}
                                    className="text-sm resize-none"
                                  />
                                  <NoteAttentionPicker
                                    value={editingNoteColor}
                                    onChange={setEditingNoteColor}
                                    testIdPrefix={`activity-pinned-note-edit-color-${note.id}`}
                                    compact
                                  />
                                  <div className="flex gap-2 justify-end">
                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleCancelEditNote}>Cancel</Button>
                                    <Button size="sm" className="h-7 text-xs" disabled={savingEdit || !editingNoteContent.trim()} onClick={() => handleSaveEditNote(note.id)}>
                                      {savingEdit ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.description || note.content}</p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <p className="text-xs text-slate-400">{note.created_at ? formatETLocale(note.created_at) : ''}</p>
                                    {note.user_name && <span className="text-xs text-slate-400">— {note.user_name}</span>}
                                    {note.is_internal && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-600">Internal</Badge>}
                                    {att && (
                                      <span
                                        className={`text-[9px] uppercase px-1.5 py-[1px] rounded border ${att.badge}`}
                                        data-testid={`activity-pinned-note-attention-${note.id}`}
                                      >
                                        {att.label}
                                      </span>
                                    )}
                                    {note.edited && (
                                      <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title={note.edited_by ? `Edited by ${note.edited_by}` : 'Edited'}>
                                        Edited{note.edited_at ? ` · ${formatETLocale(note.edited_at)}` : ''}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            {!isEditing && (
                              <div className="flex items-center gap-1 shrink-0">
                                <button type="button" onClick={() => handleTogglePinNote(note)} disabled={pinningNoteId === note.id} className="p-1.5 rounded hover:bg-cyan-100 text-slate-500 hover:text-cyan-700 disabled:opacity-50" title="Unpin" data-testid={`activity-pinned-note-unpin-${note.id}`}>
                                  {pinningNoteId === note.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PinOff className="h-3.5 w-3.5" />}
                                </button>
                                {modifiable && (
                                  <button type="button" onClick={() => handleStartEditNote(note)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800" title="Edit note" data-testid={`activity-pinned-note-edit-${note.id}`}>
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <button type="button" onClick={() => setNoteToDelete(note)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600" title={isNoteP ? 'Delete note' : 'Delete activity'} data-testid={`activity-pinned-note-delete-${note.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1" data-testid="activity-timeline-unified">
                {(() => {
                  const financeTypes = ['offers_sent', 'offer_selected', 'loan_application_deleted', 'loan_application_submitted'];
                  const filtered = activities.filter(a => !financeTypes.includes(a.activity_type));
                  // v3.15-1129 — Pin / Delete now apply to EVERY activity row
                  // (per spec "applies to all activity on the activity block, not just notes").
                  // Edit remains note-only because system events have
                  // auto-generated descriptions that aren't meaningfully editable.
                  const noteTypes = ['note_added', 'internal_note_added', 'note'];
                  // Hide pinned items from the regular timeline (they show in
                  // the Pinned section above instead) — applies to ANY activity
                  // type now, not just notes.
                  const visibleActivities = filtered.filter(a => !a.is_pinned);
                  return visibleActivities.length === 0 ? (
                    <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                      No activity yet
                    </div>
                  ) : (
                    visibleActivities.map((activity, index) => {
                      const isNote = noteTypes.includes(activity.activity_type) || activity.type === 'note';
                      const isEditing = isNote && editingNoteId === activity.id;
                      const modifiable = canModifyNote(activity);  // edit gate (notes only)
                      // v3.21-1667 · Attention_color visual treatment on note rows only.
                      const att = isNote ? getAttentionStyle(activity.attention_color) : null;
                      const rowExtra = att ? `${att.tint} ${att.accent} rounded-md pl-2` : '';
                      // v3.21-1814 · Only PRIMARY communication rows are
                      // clickable.  Lifecycle/status rows (email_opened,
                      // sms_delivered, call_outcome_logged, call_note,
                      // communication_ref) enrich an existing logical
                      // communication — they are NOT their own
                      // clickable communications, per v1814 spec.
                      const PRIMARY_COMM_TYPES = new Set([
                        'email','email_sent','email_received',
                        'sms','sms_sent','sms_received',
                        'call_completed','call_missed','call_voicemail',
                        'call_initiated','call_inbound',
                        'communication_added',
                      ]);
                      const isCommRow = PRIMARY_COMM_TYPES.has(activity.activity_type) || PRIMARY_COMM_TYPES.has(activity.type);
                      const commClickProps = isCommRow ? {
                        role: 'button',
                        tabIndex: 0,
                        onClick: () => openCommunicationHistory(),
                        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCommunicationHistory(); } },
                        title: 'Open Communication History',
                        'data-testid-comm-click': `activity-comm-click-${activity.id}`,
                      } : {};
                      return (
                      <div 
                        key={activity.id || index} 
                        {...commClickProps}
                        className={`flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0 group ${rowExtra} ${isCommRow ? 'cursor-pointer hover:bg-slate-50 rounded' : ''}`}
                        data-testid={isNote ? `activity-note-${activity.id}` : `activity-row-${activity.id}`}
                        data-attention-color={att ? att.color : 'neutral'}
                        data-comm-row={isCommRow ? 'true' : 'false'}
                      >
                        <div className={`mt-0.5 p-1.5 rounded ${
                          activity.type === 'email' || activity.activity_type === 'email' ? 'bg-blue-100' :
                          activity.type === 'sms' || activity.activity_type === 'sms' ? 'bg-green-100' :
                          activity.type === 'payment' || activity.activity_type === 'payment' ? 'bg-amber-100' :
                          activity.type === 'note' || activity.activity_type === 'note' ? 'bg-slate-100' :
                          activity.activity_type === 'terms_approved_by_partner' ? 'bg-emerald-100' :
                          activity.activity_type === 'status_change' ? 'bg-purple-100' :
                          activity.activity_type === 'call_initiated' || activity.activity_type === 'call_completed' ? 'bg-orange-100' :
                          activity.activity_type === 'call_outcome_logged' ? 'bg-orange-100' :
                          // v3.15-930: appointment activities render with an indigo badge.
                          (activity.activity_type === 'appointment_scheduled' || activity.type === 'appointment_scheduled') ? 'bg-indigo-100' :
                          (activity.activity_type === 'appointment_updated' || activity.type === 'appointment_updated') ? 'bg-indigo-50' :
                          (activity.activity_type === 'appointment_rescheduled' || activity.type === 'appointment_rescheduled') ? 'bg-amber-100' :
                          (activity.activity_type === 'appointment_completed' || activity.type === 'appointment_completed') ? 'bg-emerald-100' :
                          (activity.activity_type === 'appointment_no_show' || activity.type === 'appointment_no_show') ? 'bg-red-100' :
                          (activity.activity_type === 'appointment_cancelled' || activity.type === 'appointment_cancelled') ? 'bg-slate-100' :
                          (activity.activity_type === 'appointment_link_sent' || activity.type === 'appointment_link_sent') ? 'bg-indigo-100' :
                          (activity.activity_type === 'appointment_booked' || activity.type === 'appointment_booked') ? 'bg-emerald-100' :
                          (activity.activity_type === 'application_concierge_assigned' || activity.type === 'application_concierge_assigned') ? 'bg-cyan-100' :
                          'bg-slate-100'
                        }`}>
                          {(activity.type === 'email' || activity.activity_type === 'email') ? <Mail className="h-3.5 w-3.5 text-blue-600" /> :
                           (activity.type === 'sms' || activity.activity_type === 'sms') ? <Phone className="h-3.5 w-3.5 text-green-600" /> :
                           (activity.type === 'payment' || activity.activity_type === 'payment') ? <DollarSign className="h-3.5 w-3.5 text-amber-600" /> :
                           (activity.activity_type === 'call_initiated' || activity.activity_type === 'call_completed' || activity.activity_type === 'call_outcome_logged') ? <PhoneOutgoing className="h-3.5 w-3.5 text-orange-600" /> :
                           /* v3.15-930: calendar icon for any appointment_* activity (including link sent) */
                           (String(activity.activity_type || activity.type || '').startsWith('appointment_')) ? <Calendar className="h-3.5 w-3.5 text-indigo-600" data-testid={`activity-appointment-icon-${activity.activity_type || activity.type}`} /> :
                           /* v3.15-933: concierge claim → user icon in cyan */
                           (activity.activity_type === 'application_concierge_assigned' || activity.type === 'application_concierge_assigned') ? <UserCheck className="h-3.5 w-3.5 text-cyan-600" data-testid="activity-concierge-icon" /> :
                           <Clock className="h-3.5 w-3.5 text-slate-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="space-y-2" data-testid={`activity-note-edit-inline-${activity.id}`}>
                              <Textarea
                                value={editingNoteContent}
                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                rows={3}
                                className="text-sm resize-none"
                                data-testid={`activity-note-edit-textarea-${activity.id}`}
                              />
                              <NoteAttentionPicker
                                value={editingNoteColor}
                                onChange={setEditingNoteColor}
                                testIdPrefix={`activity-note-edit-color-${activity.id}`}
                                compact
                              />
                              <div className="flex gap-2 justify-end">
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleCancelEditNote} data-testid={`activity-note-edit-cancel-${activity.id}`}>Cancel</Button>
                                <Button size="sm" className="h-7 text-xs" disabled={savingEdit || !editingNoteContent.trim()} onClick={() => handleSaveEditNote(activity.id)} data-testid={`activity-note-edit-save-${activity.id}`}>
                                  {savingEdit ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-slate-700">{activity.description || activity.content || activity.title}
                                {activity.edited && (
                                  <span className="ml-2 align-middle text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title={activity.edited_by ? `Edited by ${activity.edited_by}` : 'Edited'}>edited</span>
                                )}
                                {att && (
                                  <span
                                    className={`ml-2 align-middle text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${att.badge}`}
                                    data-testid={`activity-note-attention-${activity.id}`}
                                  >
                                    {att.label}
                                  </span>
                                )}
                              </p>
                              {/* v3.18-1250 CALL-TRACK-002 — enriched inbound-call
                                  block. Everything (Answered By, Duration,
                                  Outcome) shown on ONE timeline row so the
                                  activity feed stays clean. Notes render
                                  via the existing italic <p> below. */}
                              {activity.activity_type === 'call_inbound' && activity.metadata?.outcome && (
                                <div className="mt-1 text-xs text-slate-600 space-y-0.5" data-testid={`inbound-call-enriched-${activity.id}`}>
                                  {activity.metadata.answered_by_user_name && (
                                    <div><span className="text-slate-400">Answered by</span> <span className="font-medium text-slate-700">{activity.metadata.answered_by_user_name}</span></div>
                                  )}
                                  {activity.metadata.duration_sec != null && (
                                    <div><span className="text-slate-400">Duration</span> <span className="font-medium text-slate-700">{Math.floor(activity.metadata.duration_sec / 60)}m {activity.metadata.duration_sec % 60}s</span></div>
                                  )}
                                  <div><span className="text-slate-400">Outcome</span> <span className="font-medium text-slate-700">{activity.metadata.outcome_label || activity.metadata.outcome}</span></div>
                                </div>
                              )}
                              {activity.metadata?.notes && (
                                <p className="text-xs text-slate-500 mt-0.5 italic">"{activity.metadata.notes}"</p>
                              )}
                              <p className="text-xs text-slate-400 mt-0.5">
                                {activity.created_at ? formatETLocale(activity.created_at) : ''}
                                {activity.user_name && ` — ${activity.user_name}`}
                              </p>
                              {/* v3.18-1260 PAYMENT-005 — View Receipt on payment activities */}
                              {(activity.activity_type === 'payment_approved' ||
                                activity.activity_type === 'payment_declined' ||
                                activity.activity_type === 'payment_initiated') &&
                                activity.payment_transaction_id ? (
                                  <button
                                    onClick={() => setTimelineReceiptTxId(activity.payment_transaction_id)}
                                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                                    data-testid={`timeline-view-receipt-${activity.id}`}
                                  >
                                    <ReceiptIcon className="h-3 w-3" />View Receipt
                                  </button>
                              ) : null}
                            </>
                          )}
                        </div>
                        {/* v3.15-1129 — Pin + Delete on EVERY activity row
                            (per spec: "applies to all activity on the
                            activity block, not just the notes tab").
                            Edit remains note-only since system-generated
                            events have auto-generated descriptions. */}
                        {!isEditing && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleTogglePinNote(activity)}
                              disabled={pinningNoteId === activity.id}
                              className={`p-1.5 rounded text-slate-500 hover:text-cyan-700 disabled:opacity-50 ${activity.is_pinned ? 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100' : 'hover:bg-cyan-50'}`}
                              title={activity.is_pinned ? 'Unpin' : 'Pin'}
                              data-testid={`activity-pin-${activity.id}`}
                            >
                              {pinningNoteId === activity.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (activity.is_pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />)}
                            </button>
                            {isNote && modifiable && (
                              <button
                                type="button"
                                onClick={() => handleStartEditNote(activity)}
                                className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                                title="Edit note"
                                data-testid={`activity-note-edit-${activity.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setNoteToDelete(activity)}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"
                              title={isNote ? 'Delete note' : 'Delete activity'}
                              data-testid={`activity-delete-${activity.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      );
                    })
                  );
                })()}
              </div>
              {/* v3.15: Add Note in activity tab for ALL users (append-only) */}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <input
                    ref={addNoteInputRef}
                    type="text"
                    value={addNoteContent}
                    onChange={(e) => setAddNoteContent(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 h-8 text-xs border border-slate-200 rounded px-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    data-testid="activity-add-note-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddActivityNote(); }}
                  />
                  {/* v3.15-751: Ghost Rewrite is a SEPARATE draft-only action */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => handleGhostRewrite('input')}
                    disabled={rewritingNote || addingNote || !addNoteContent.trim()}
                    data-testid="activity-ghost-rewrite-btn"
                    title="Ghost rewrites your draft — review and edit before Add Note"
                  >
                    {rewritingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : '✨'}
                    <span className="ml-1">{rewritingNote ? 'Rewriting…' : 'Ghost Rewrite'}</span>
                  </Button>
                  <Button size="sm" className="h-8 text-xs" onClick={handleAddActivityNote} disabled={addingNote || rewritingNote || !addNoteContent.trim()} data-testid="activity-add-note-btn">
                    {addingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                    Add Note
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-[10px] text-slate-500">Ghost Rewrite drafts only — nothing is posted until you click Add Note.</span>
                </div>
              </div>
            </TabsContent>

            {/* v3.14-414: Appointment Tab — moved here from DealDashboard */}
            <TabsContent value="appointment" className="mt-0">
              <div className="py-4 px-2" data-testid="appointment-tab-content">
                {client?.appointment_event_id ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 max-w-md">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <span className="font-semibold text-emerald-700 text-sm">Appointment Scheduled</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Date / Time</span>
                        <span className="font-medium text-slate-800">
                          {client?.appointment_datetime ? new Date(client.appointment_datetime).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}
                        </span>
                      </div>
                      {client?.appointment_rep && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Rep</span>
                          <span className="font-medium text-slate-800">{client.appointment_rep}</span>
                        </div>
                      )}
                      {client?.appointment_notes && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Notes</span>
                          <span className="text-slate-600">{client.appointment_notes}</span>
                        </div>
                      )}
                    </div>
                    {client?.appointment_event_link && (
                      <a href={client.appointment_event_link} target="_blank" rel="noopener noreferrer"
                        className="mt-3 flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-medium text-xs">
                        <Calendar className="h-3 w-3" /> View in Calendar
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-slate-400">
                    No appointment scheduled
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Finance History Tab — only finance-related entries */}
            <TabsContent value="finance-history" className="mt-0">
              <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1" data-testid="finance-history-timeline">
                {(() => {
                  const financeTypes = ['offers_sent', 'offer_selected', 'loan_application_deleted', 'loan_application_submitted', 'card_revealed', 'payment_initiated', 'payment_approved', 'payment_declined', 'payment_error', 'payment_updated'];
                  const filtered = activities.filter(a => financeTypes.includes(a.activity_type));
                  return filtered.length === 0 ? (
                    <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                      No finance history yet
                    </div>
                  ) : (
                    filtered.map((activity, index) => (
                      <div 
                        key={activity.id || index} 
                        className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0"
                      >
                        <div className={`mt-0.5 p-1.5 rounded ${
                          activity.activity_type === 'offers_sent' ? 'bg-green-100' :
                          activity.activity_type === 'offer_selected' ? 'bg-blue-100' :
                          activity.activity_type === 'loan_application_submitted' ? 'bg-amber-100' :
                          activity.activity_type === 'loan_application_deleted' ? 'bg-red-100' :
                          'bg-slate-100'
                        }`}>
                          {activity.activity_type === 'offers_sent' ? <DollarSign className="h-3.5 w-3.5 text-green-600" /> :
                           activity.activity_type === 'offer_selected' ? <DollarSign className="h-3.5 w-3.5 text-blue-600" /> :
                           activity.activity_type === 'loan_application_submitted' ? <DollarSign className="h-3.5 w-3.5 text-amber-600" /> :
                           <DollarSign className="h-3.5 w-3.5 text-red-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700">{activity.description || activity.content || activity.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {activity.created_at ? formatETLocale(activity.created_at) : ''}
                            {activity.user_name && ` — ${activity.user_name}`}
                          </p>
                        </div>
                      </div>
                    ))
                  );
                })()}
              </div>
            </TabsContent>

            {/* Notes Tab — v3.15-1124: Pinned notes + Edit + Delete */}
            <TabsContent value="notes" className="mt-0">
              <div data-testid="notes-tab-content">
                {(() => {
                  const noteTypes = ['note_added', 'internal_note_added', 'note'];
                  const allNotes = activities.filter(a => noteTypes.includes(a.activity_type) || a.type === 'note');
                  const pinnedNotes = allNotes.filter(a => a.is_pinned);
                  const unpinnedNotes = allNotes.filter(a => !a.is_pinned);

                  const renderNote = (note, ctx) => {
                    const isEditing = editingNoteId === note.id;
                    const modifiable = canModifyNote(note);
                    const isPinning = pinningNoteId === note.id;
                    const isDeletingThis = deletingNoteId === note.id;
                    // v3.21-1667 · attention_color visual treatment for the Notes tab.
                    const att = getAttentionStyle(note.attention_color);
                    const baseBg = note.is_internal ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100';
                    const bg = att ? `${att.tint} ${att.accent} border-slate-100` : baseBg;
                    const pinnedRing = ctx === 'pinned' ? 'ring-1 ring-cyan-200' : '';
                    return (
                      <div
                        key={note.id || `${ctx}-${note.created_at}`}
                        className={`flex items-start gap-3 py-2.5 px-3 rounded-lg border ${bg} ${pinnedRing}`}
                        data-testid={ctx === 'pinned' ? `pinned-note-${note.id}` : `note-${note.id}`}
                        data-attention-color={att ? att.color : 'neutral'}
                      >
                        <div className={`mt-0.5 p-1.5 rounded ${note.is_internal ? 'bg-amber-200' : 'bg-blue-100'}`}>
                          <FileText className="h-3.5 w-3.5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="space-y-2" data-testid={`note-edit-${note.id}`}>
                              <Textarea
                                value={editingNoteContent}
                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                rows={3}
                                className="text-sm resize-none"
                                data-testid={`note-edit-textarea-${note.id}`}
                              />
                              <NoteAttentionPicker
                                value={editingNoteColor}
                                onChange={setEditingNoteColor}
                                testIdPrefix={`note-edit-color-${note.id}`}
                                compact
                              />
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={handleCancelEditNote}
                                  data-testid={`note-edit-cancel-${note.id}`}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 text-xs"
                                  disabled={savingEdit || !editingNoteContent.trim()}
                                  onClick={() => handleSaveEditNote(note.id)}
                                  data-testid={`note-edit-save-${note.id}`}
                                >
                                  {savingEdit ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.description || note.content}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <p className="text-xs text-slate-400">
                                  {note.created_at ? formatETLocale(note.created_at) : ''}
                                </p>
                                {note.user_name && <span className="text-xs text-slate-400">— {note.user_name}</span>}
                                {note.is_internal && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-600">Internal</Badge>}
                                {att && (
                                  <span
                                    className={`text-[9px] uppercase px-1.5 py-[1px] rounded border ${att.badge}`}
                                    data-testid={`note-attention-${note.id}`}
                                  >
                                    {att.label}
                                  </span>
                                )}
                                {note.edited && (
                                  <span
                                    className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                                    title={note.edited_by ? `Edited by ${note.edited_by}${note.edited_at ? ' at ' + formatETLocale(note.edited_at) : ''}` : 'Edited'}
                                    data-testid={`note-edited-badge-${note.id}`}
                                  >
                                    Edited{note.edited_at ? ` · ${formatETLocale(note.edited_at)}` : ''}
                                  </span>
                                )}
                                {note.is_pinned && ctx !== 'pinned' && (
                                  <span className="text-[10px] text-cyan-700 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5 inline-flex items-center gap-0.5">
                                    <Pin className="h-2.5 w-2.5" /> Pinned
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        {!isEditing && (
                          <div className="flex items-center gap-1 shrink-0">
                            {/* Pin/unpin — available to any user with read access */}
                            <button
                              type="button"
                              onClick={() => handleTogglePinNote(note)}
                              disabled={isPinning}
                              className={`p-1.5 rounded hover:bg-cyan-100 text-slate-500 hover:text-cyan-700 disabled:opacity-50`}
                              title={note.is_pinned ? 'Unpin note' : 'Pin note'}
                              data-testid={`note-pin-toggle-${note.id}`}
                            >
                              {isPinning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (note.is_pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />)}
                            </button>
                            {modifiable && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleStartEditNote(note)}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                                  title="Edit note"
                                  data-testid={`note-edit-btn-${note.id}`}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setNoteToDelete(note)}
                                  className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"
                                  title="Delete note"
                                  disabled={isDeletingThis}
                                  data-testid={`note-delete-btn-${note.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <>
                      {/* Pinned Notes section */}
                      {pinnedNotes.length > 0 && (
                        <div className="mb-3" data-testid="pinned-notes-section">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Pin className="h-3 w-3 text-cyan-700" />
                            <Label className="text-[10px] text-cyan-700 uppercase tracking-wide font-semibold">Pinned Notes</Label>
                            <span className="text-[10px] text-slate-400">· {pinnedNotes.length}</span>
                          </div>
                          <div className="space-y-2">
                            {pinnedNotes.map(n => renderNote(n, 'pinned'))}
                          </div>
                        </div>
                      )}

                      {/* All Notes timeline */}
                      <div className="max-h-[350px] overflow-y-auto space-y-2 pr-1 mb-3" data-testid="notes-timeline">
                        {allNotes.length === 0 ? (
                          <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                            No notes yet — add one below
                          </div>
                        ) : unpinnedNotes.length === 0 ? (
                          <div className="text-[11px] text-slate-400 text-center py-2 italic">
                            All notes are pinned
                          </div>
                        ) : (
                          unpinnedNotes.map(n => renderNote(n, 'timeline'))
                        )}
                      </div>
                    </>
                  );
                })()}
                {/* Add Note — append-only for all users */}
                <div className="border-t pt-3 relative z-10" data-testid="add-note-section">
                  <div className="flex items-center gap-2 mb-1">
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Add Note</Label>
                    {isInternal() && (
                      <label className="flex items-center gap-1 text-[10px] text-amber-600 cursor-pointer">
                        <input type="checkbox" checked={noteIsInternal} onChange={(e) => setNoteIsInternal(e.target.checked)} className="h-3 w-3" data-testid="note-internal-checkbox" />
                        Internal (PTC Only)
                      </label>
                    )}
                  </div>
                  <Textarea
                    ref={addNoteTextareaRef}
                    value={addNoteContent}
                    onChange={(e) => setAddNoteContent(e.target.value)}
                    placeholder="Type your note here..."
                    rows={3}
                    className="mt-1 text-sm resize-none"
                    data-testid="add-note-textarea"
                  />
                  {/* v3.21-1685 · Inline attention-color picker on create.
                      Follows the same NEUTRAL/GREEN/YELLOW/RED semantics
                      as the edit surface.  Pure attention — writes only
                      to the note's attention_color (isolated from Deal
                      lifecycle / funding / paid_in_full / assignment). */}
                  <div className="mt-2" data-testid="add-note-color-row">
                    <NoteAttentionPicker
                      value={addNoteColor}
                      onChange={setAddNoteColor}
                      testIdPrefix="note-color"
                      compact
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2 gap-2">
                    <span className="text-[10px] text-slate-500">Ghost Rewrite drafts only — Add Note posts as written.</span>
                    <div className="flex gap-2">
                      {/* v3.15-751: Ghost Rewrite — drafts only, no auto-post */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => handleGhostRewrite('textarea')}
                        disabled={rewritingNote || addingNote || !addNoteContent.trim()}
                        data-testid="add-note-ghost-rewrite-btn"
                        title="Ghost rewrites your draft — review and edit before Add Note"
                      >
                        {rewritingNote ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <span className="mr-1">✨</span>}
                        {rewritingNote ? 'Rewriting…' : 'Ghost Rewrite'}
                      </Button>
                      <Button size="sm" className="text-xs h-7" onClick={handleAddActivityNote} disabled={addingNote || rewritingNote || !addNoteContent.trim()} data-testid="add-note-submit-btn">
                        {addingNote ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                        Add Note
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Communications Tab */}
            <TabsContent value="communications" className="mt-0">
              {/* v3.18-1385 GMAIL-005 — Gmail thread history scoped to this client */}
              <div className="mb-4">
                <ClientGmailThreadPanel clientId={id} />
              </div>
              {/* v3.18-1296 — embedded Contact Directory for this client */}
              <div className="mb-4">
                <ClientCommunicationsTab
                  clientId={id}
                  companyId={client?.company_id}
                  canWrite
                  compact={false}
                />
              </div>
              {/* v3.18-1300 — Unified chronological communication timeline */}
              <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
                <UnifiedTimeline clientId={id} />
              </div>
              <ClientCommunications
                clientId={id}
                clientEmail={client?.email}
                clientName={client?.full_name}
                clientPhone={client?.mobile_phone || client?.phone}
              />
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks" className="mt-0">
              <div className="flex items-center justify-end mb-3">
                <Dialog open={isTaskOpen} onOpenChange={setIsTaskOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-accent hover:bg-gold-dark" data-testid="add-task-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Task</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={newTask.title}
                          onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                          required
                          data-testid="task-title-input"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newTask.description}
                          onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Assigned Users</Label>
                        {/* v3.15-1080: multi-assignee picker. Any selected
                            user can complete the task. External Email
                            assignment remains a separate path below. */}
                        <UserMultiSelect
                          users={assignableUsers}
                          companies={companies || []}
                          value={newTask.assigned_users}
                          onChange={(ids) => setNewTask({ ...newTask, assigned_users: ids })}
                          placeholder="Assign to one or more users (default: yourself)"
                          testId="task-assignees"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="task-assign-external-toggle"
                            checked={newTask.assigned_to === 'external'}
                            onChange={(e) => setNewTask({
                              ...newTask,
                              assigned_to: e.target.checked ? 'external' : '',
                              external_email: e.target.checked ? newTask.external_email : '',
                            })}
                            data-testid="task-external-toggle"
                          />
                          <label htmlFor="task-assign-external-toggle" className="text-xs text-slate-500 cursor-pointer">
                            Also notify an external email
                          </label>
                        </div>
                      </div>
                      {newTask.assigned_to === 'external' && (
                        <div>
                          <Label>External Email</Label>
                          <Input
                            type="email"
                            value={newTask.external_email || ''}
                            onChange={(e) => setNewTask({ ...newTask, external_email: e.target.value })}
                            placeholder="email@example.com"
                            required
                            data-testid="task-external-email-input"
                          />
                          <p className="text-[10px] text-slate-400 mt-0.5">Email notification will be sent with task details</p>
                        </div>
                      )}
                      <div>
                        <Label>Due Date</Label>
                        <Input
                          type="date"
                          value={newTask.due_date}
                          onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                          data-testid="task-due-date-input"
                        />
                      </div>
                      <div>
                        <Label>Due Time</Label>
                        <Input
                          type="time"
                          value={newTask.due_time}
                          onChange={(e) => setNewTask({ ...newTask, due_time: e.target.value })}
                          data-testid="task-due-time-input"
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setIsTaskOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-accent hover:bg-gold-dark" data-testid="submit-task-btn">
                          Create Task
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {tasks.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No tasks yet</p>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-4 rounded-lg border ${
                        task.status === 'completed' ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-200'
                      }`}
                      data-testid={`task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className={`font-medium ${task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {task.title}
                          </p>
                          {task.description && (
                            <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                            {/* v3.15-1080: render every assigned user.
                                Falls back to the legacy single-assignee
                                name for tasks predating multi-assignee. */}
                            {(() => {
                              const names = (task.assigned_users_names && task.assigned_users_names.length)
                                ? task.assigned_users_names
                                : (task.assigned_to_name ? [task.assigned_to_name] : []);
                              if (!names.length) return null;
                              return (
                                <span
                                  className="flex items-center gap-1 flex-wrap"
                                  data-testid={`task-${task.id}-assignees`}
                                >
                                  <span className="text-slate-400">Assigned To:</span>
                                  {names.map((n, i) => (
                                    <span
                                      key={`${task.id}-a-${i}`}
                                      className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px]"
                                    >
                                      {n}
                                    </span>
                                  ))}
                                </span>
                              );
                            })()}
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {format(new Date(task.due_date), 'MMM d')}
                              </span>
                            )}
                            {task.status === 'completed' && task.completed_by_name && (
                              <span
                                className="text-emerald-600"
                                data-testid={`task-${task.id}-completed-by`}
                              >
                                Completed by {task.completed_by_name}
                                {task.completed_at ? ` · ${format(new Date(task.completed_at), 'MMM d, h:mm a')}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {task.status !== 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCompleteTask(task.id)}
                            data-testid={`complete-task-${task.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-0">
              <div className="flex items-center justify-end mb-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleUploadDocument}
                    data-testid="upload-document-input"
                  />
                  <Button size="sm" className="bg-accent hover:bg-gold-dark" asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </span>
                  </Button>
                </label>
              </div>
              {documents.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No documents yet</p>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-200"
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-100">
                          <FileText className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{doc.filename}</p>
                          <p className="text-xs text-slate-400">
                            {doc.uploaded_by_name} • {formatET(doc.uploaded_at, 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="h-4 w-4 text-slate-600" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* v3.15-670: Package Tracking Tab */}
            <TabsContent value="tracking" className="mt-0">
              {trackingData ? (
                <div className="space-y-4 py-2" data-testid="tracking-details">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">Tracking #</p>
                      <p className="text-sm font-semibold text-slate-800" data-testid="tracking-number">{trackingData.tracking_number}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">Carrier</p>
                      <p className="text-sm font-medium text-slate-700">{trackingData.carrier || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">Status</p>
                      <Badge className={`text-xs px-2 py-0.5 ${
                        trackingData.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        trackingData.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                        trackingData.status === 'out_for_delivery' ? 'bg-amber-100 text-amber-800' :
                        'bg-slate-100 text-slate-600'
                      }`} variant="secondary" data-testid="tracking-status-badge">{trackingData.status_label || trackingData.status}</Badge>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">Est. Delivery</p>
                      <p className="text-sm font-medium text-slate-700">{trackingData.est_delivery ? format(new Date(trackingData.est_delivery), 'MMM d, yyyy') : '—'}</p>
                    </div>
                  </div>
                  {trackingData.location && (
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <MapPin className="h-3.5 w-3.5 text-slate-400" />
                      <span data-testid="tracking-location">{trackingData.location}</span>
                      {trackingData.last_update && (
                        <span className="text-[10px] text-slate-400 ml-2">Updated {formatET(trackingData.last_update, 'MMM d, h:mm a')}</span>
                      )}
                    </div>
                  )}
                  {/* Timeline */}
                  {trackingData.timeline && trackingData.timeline.length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-[10px] text-slate-400 uppercase font-medium mb-2">Shipment Timeline</p>
                      <div className="space-y-2" data-testid="tracking-timeline">
                        {[...trackingData.timeline].reverse().map((evt, i) => (
                          <div key={evt.datetime || `evt-${i}`} className="flex items-start gap-3 text-sm">
                            <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                              evt.status === 'delivered' ? 'bg-green-500' :
                              evt.status === 'in_transit' ? 'bg-blue-500' :
                              evt.status === 'out_for_delivery' ? 'bg-amber-500' :
                              'bg-slate-300'
                            }`} />
                            <div>
                              <p className="text-slate-700">{evt.message}</p>
                              <p className="text-xs text-slate-400">{evt.location}{evt.datetime ? ` — ${formatET(evt.datetime, 'MMM d, h:mm a')}` : ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-4 space-y-3" data-testid="tracking-create-form">
                  <p className="text-sm text-slate-500">No tracking info yet. Enter a tracking number to start tracking.</p>
                  <div className="flex items-center gap-2 max-w-lg">
                    <Input placeholder="Tracking number" value={trackingInput} onChange={e => setTrackingInput(e.target.value)} className="h-8 text-sm" data-testid="tracking-number-input" />
                    <Input placeholder="Carrier (optional)" value={trackingCarrier} onChange={e => setTrackingCarrier(e.target.value)} className="h-8 text-sm w-40" data-testid="tracking-carrier-input" />
                    <Button size="sm" className="h-8 bg-accent hover:bg-gold-dark whitespace-nowrap" onClick={handleCreateTracker} disabled={creatingTracker || !trackingInput.trim()} data-testid="create-tracker-btn">
                      {creatingTracker ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Truck className="h-3 w-3 mr-1" />}
                      Create Tracker
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
            {/* v3.21-1705 OWNERSHIP-VIS-001 — visible to authorized internal
                PTC users, external partners under timeshare_exit companies,
                and anyone viewing a client that already has ownership data.
                Per-client access enforced by backend. */}
            {canSeeOwnershipTab && (
              <TabsContent value="ownership" className="mt-0" data-testid="ownership-tab-content">
                <div className="px-4 pb-4 pt-2">
                  <TimeshareOwnershipTab clientId={id} />
                </div>
              </TabsContent>
            )}
            {/* v3.15-1230 SMART-INTAKE-003 — Payment Methods surface for
                all clients. Reads from client_credit_cards +
                client_ach_accounts populated by Smart Client Intake. */}
            <TabsContent value="payment-methods" className="mt-0" data-testid="payment-methods-tab-content">
              <div className="px-4 pb-4 pt-2">
                <PaymentMethodsTab clientId={id} />
              </div>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* PTC-Only Sections — below Activity tabs */}
      {client?.company_name?.toLowerCase().includes('prestigious travel club') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 max-w-5xl" data-testid="ptc-only-sections">
          <PTCDealFinancials
            clientId={client?.id}
            clientName={client?.full_name}
            dealAmount={client?.deal_amount || client?.service_price || 0}
          />
          <Card data-testid="expense-ledger-card">
            <CardHeader className="py-1.5 px-3">
              <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                <Receipt className="h-3 w-3" /> Deal Expenses
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-2">
              <DealExpenseLedger clientId={id} dealAmount={client.deal_amount} />
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/30" data-testid="internal-lending-card">
            <CardHeader className="py-1.5 px-3">
              <CardTitle className="text-xs font-semibold flex items-center gap-1.5 text-red-800">
                <Lock className="h-3 w-3" /> Internal Lending
                <Badge variant="outline" className="text-red-600 border-red-300 text-[9px] px-1 py-0">PTC Only</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-2 space-y-1.5 cursor-pointer hover:bg-slate-50/50 rounded transition-colors text-xs" onClick={() => setIsEditOpen(true)} data-testid="lending-fields">
              <div className="flex justify-between"><span className="text-slate-500">Institution</span><span className="font-medium">{client.lending_institution || '-'}</span></div>
              {client.lender_contact_name && <div className="flex justify-between"><span className="text-slate-500">Contact</span><span className="font-medium">{client.lender_contact_name}</span></div>}
              {client.internal_notes && <div className="pt-1 border-t"><span className="text-slate-500">Notes: </span><span>{client.internal_notes}</span></div>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* v3.14-301: Credit Applications REMOVED — financing fields now in Client Info */}

      {/* v3.21-1742: Compensation Visibility block REMOVED from the Deal
          page.  This was the visible V1 read-only projection Card rendered
          via <DealCompensationVisibility dealId={id} />.  It was never
          operationally useful and consumed prime deal-page vertical space
          with no user value.  The v3.15-1221 CRM-INT-003 <DealCompensation
          Visibility /> component file remains in the repo (dormant/
          unreferenced) but no active Deal-page rendering path exists.
          Compensation Tracking (PTC Only) + Payroll Settings (PTC Only)
          Cards below are preserved untouched. */}

      {/* v3.15-1226 OPS-001 — Priority Queue panel (current + history + add). */}
      {/* v3.15-1235 — Priority Queue client panel removed. Priority items now surface inside the Ghost Daily Organizer. */}

      {/* v3.14-236: Compensation & Payroll — very bottom */}
      {isInternal() && (
        <div className="grid grid-cols-2 gap-3">
          {/* Compensation Tracking (PTC Only) */}
          <Card className="border-red-200 bg-red-50/30" data-testid="compensation-card">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-800">
                <Calculator className="h-4 w-4" /> Compensation Tracking (PTC Only)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-sm font-medium">Commission Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    defaultValue={client.commission_rate || 0.5}
                    onBlur={async (e) => {
                      const rate = parseFloat(e.target.value);
                      if (!isNaN(rate) && rate !== client.commission_rate) {
                        const updates = { commission_rate: rate || null };
                        if (rate && client.deal_amount) {
                          updates.estimated_pay = Math.round(client.deal_amount * rate / 100 * 100) / 100;
                        } else {
                          updates.estimated_pay = null;
                        }
                        await api.put(`/clients/${id}`, updates);
                        setClient(prev => ({ ...prev, ...updates }));
                        toast.success('Commission updated');
                      }
                    }}
                    data-testid="commission-rate-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Estimated Pay (auto-calculated)</Label>
                  <Input
                    value={(() => {
                      const rate = client.commission_rate || client.commission_rate === 0 ? Number(client.commission_rate) : 0.5;
                      const amt = Number(client.deal_amount) || 0;
                      if (rate && amt) {
                        const pay = Math.round(amt * rate / 100 * 100) / 100;
                        return `$${pay.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                      }
                      return '-';
                    })()}
                    readOnly
                    className="bg-slate-100"
                    data-testid="estimated-pay-display"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Compensation Notes</Label>
                <Textarea
                  defaultValue={client.compensation_notes || ''}
                  onBlur={async (e) => {
                    if (e.target.value !== (client.compensation_notes || '')) {
                      await api.put(`/clients/${id}`, { compensation_notes: e.target.value });
                      setClient(prev => ({ ...prev, compensation_notes: e.target.value }));
                      toast.success('Notes saved');
                    }
                  }}
                  rows={3}
                  data-testid="compensation-notes"
                />
              </div>
            </CardContent>
          </Card>

          {/* Payroll Settings (PTC Only) */}
          <Card className="border-green-200 bg-green-50/30" data-testid="payroll-settings-card">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-green-800">
                <DollarSign className="h-4 w-4" /> Payroll Settings (PTC Only)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Deal Source</Label>
                  <Select
                    value={client.deal_source || ''}
                    onValueChange={async (value) => {
                      await api.put(`/clients/${id}`, { deal_source: value });
                      setClient(prev => ({ ...prev, deal_source: value }));
                      toast.success('Deal source updated');
                    }}
                  >
                    <SelectTrigger data-testid="deal-source-select">
                      <SelectValue placeholder="Select Source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easyquickfund">EasyQuickFund</SelectItem>
                      <SelectItem value="ptc_direct_sale">PTC Direct Sale</SelectItem>
                      <SelectItem value="solar">Solar Exit</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Determines commission type: EQF = Processing Pay, Direct = 15% Sales</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Vendor Payment Received Date</Label>
                  <Input
                    type="date"
                    defaultValue={client.vendor_payment_received_date || ''}
                    onBlur={async (e) => {
                      if (e.target.value !== (client.vendor_payment_received_date || '')) {
                        await api.put(`/clients/${id}`, { vendor_payment_received_date: e.target.value || null });
                        setClient(prev => ({ ...prev, vendor_payment_received_date: e.target.value }));
                        toast.success('Date updated');
                      }
                    }}
                    data-testid="vendor-payment-date"
                  />
                  <p className="text-xs text-slate-500 mt-1">Triggers funding/sales commission for payroll</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Deal Dialog */}
      <Dialog open={showCreateDealDialog} onOpenChange={setShowCreateDealDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create PTC Deal for {client?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p className="font-medium">{client?.full_name}</p>
              <p className="text-slate-500">{client?.email}</p>
              {client?.phone && (
                <PhoneClickable phone={client.phone} name={client.full_name} clientId={id} clientName={client.full_name} className="text-slate-500" disableModal={true} />
              )}
            </div>
            
            {/* Resorts */}
            <div className="space-y-2">
              <Label>Resorts *</Label>
              {newDealResorts.map((resort, idx) => (
                <div key={resort.id || `resort-${idx}`} className="flex gap-2">
                  <Input
                    value={resort.name}
                    onChange={(e) => {
                      const updated = [...newDealResorts];
                      updated[idx].name = e.target.value;
                      setNewDealResorts(updated);
                    }}
                    placeholder="Resort name"
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={resort.price}
                    onChange={(e) => {
                      const updated = [...newDealResorts];
                      updated[idx].price = e.target.value;
                      setNewDealResorts(updated);
                    }}
                    placeholder="Price"
                    className="w-28"
                  />
                  {newDealResorts.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setNewDealResorts(newDealResorts.filter((_, i) => i !== idx))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setNewDealResorts([...newDealResorts, { name: '', price: '' }])}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Resort
              </Button>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newDealNotes}
                onChange={(e) => setNewDealNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
            
            {/* Commission Role Assignments */}
            <div className="space-y-2">
              <Label className="text-green-700 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> Commission Role Assignments
              </Label>
              <p className="text-xs text-slate-500">Assign roles for commission payouts.</p>
              <div className="grid grid-cols-2 gap-3 bg-green-50/50 p-3 rounded-lg">
                <div>
                  <Label className="text-xs">Sales Rep (15%)</Label>
                  <Select
                    value={newDealCommission.sales_rep_id || 'none'}
                    onValueChange={(v) => setNewDealCommission(prev => ({ ...prev, sales_rep_id: v === 'none' ? null : v }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {ptcReps.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Manager (4%)</Label>
                  <Select
                    value={newDealCommission.manager_id || 'none'}
                    onValueChange={(v) => setNewDealCommission(prev => ({ ...prev, manager_id: v === 'none' ? null : v }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {ptcReps.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Director (3%)</Label>
                  <Select
                    value={newDealCommission.director_id || 'none'}
                    onValueChange={(v) => setNewDealCommission(prev => ({ ...prev, director_id: v === 'none' ? null : v }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {ptcReps.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Payment Method</Label>
                  <Select
                    value={newDealCommission.payment_method || 'none'}
                    onValueChange={(v) => setNewDealCommission(prev => ({ ...prev, payment_method: v === 'none' ? null : v }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Set</SelectItem>
                      <SelectItem value="ach">ACH (0%)</SelectItem>
                      <SelectItem value="credit_card">Credit Card (8%)</SelectItem>
                      <SelectItem value="financing">Financing (9%)</SelectItem>
                      <SelectItem value="financing_plus_cc">Financing + CC (17%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDealDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDeal} disabled={creatingDeal}>
              {creatingDeal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Deal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Funding Dialog */}
      <Dialog open={confirmFundingDialog} onOpenChange={setConfirmFundingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-emerald-800 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Confirm Funding
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConfirmFunding} className="space-y-4 mt-4">
            <div className="bg-emerald-50 p-3 rounded-lg text-sm text-emerald-800">
              <p className="font-medium">This will:</p>
              <ul className="list-disc list-inside mt-1 text-xs space-y-1">
                <li>Mark deal as "Funded"</li>
                <li>Set vendor payment received date</li>
                <li>Trigger commission calculations</li>
                <li>Add to settlement reports</li>
              </ul>
            </div>
            
            <div>
              <Label>Funding Date</Label>
              <Input
                type="date"
                value={confirmFundingData.funding_date}
                onChange={(e) => setConfirmFundingData({...confirmFundingData, funding_date: e.target.value})}
                required
                data-testid="funding-date-input"
              />
            </div>
            
            <div>
              <Label>Funded Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={confirmFundingData.funded_amount}
                onChange={(e) => setConfirmFundingData({...confirmFundingData, funded_amount: e.target.value})}
                placeholder={`Default: $${client?.deal_amount?.toLocaleString() || '0'}`}
                data-testid="funded-amount-input"
              />
            </div>
            
            <div>
              <Label>Credit App Confirmation # (Optional)</Label>
              <Input
                value={confirmFundingData.confirmation_number}
                onChange={(e) => setConfirmFundingData({...confirmFundingData, confirmation_number: e.target.value})}
                placeholder="Enter confirmation number"
                data-testid="confirmation-number-input"
              />
            </div>
            
            <div>
              <Label>ACH Confirmation # (Optional)</Label>
              <Input
                value={confirmFundingData.ach_confirmation_number}
                onChange={(e) => setConfirmFundingData({...confirmFundingData, ach_confirmation_number: e.target.value})}
                placeholder="Enter ACH confirmation number"
                data-testid="ach-confirmation-input"
              />
            </div>
            
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmFundingDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={confirmingFunding}
              >
                {confirmingFunding ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Confirm Funding
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* v3.21-1612 · Call Outcome Modal removed — ActiveCallPill is
          the SSOT for disposition + notes + End Call. */}

      {/* v3.15-981: Funding closure email confirmation gate */}
      <PaidInFullConfirmModal
        open={paidInFullConfirmOpen}
        onOpenChange={setPaidInFullConfirmOpen}
        clientId={id}
        onConfirmed={(data) => {
          setClient((prev) => prev ? {
            ...prev,
            paid_in_full: true,
            deal_stage: 'funded',
            funding_status: 'paid',
            is_active: false,
            closed_at: data?.closed_at || new Date().toISOString(),
          } : prev);
        }}
      />

      {/* v3.15-1069: Transactional SMS — Credit App / ACH link sends.
          Backend safety chain (routes/sms.py) blocks all sends unless
          ENABLE_TRANSACTIONAL_SMS=true. Initial deployment runs in TEST MODE. */}
      <TransactionalSmsModal
        open={txnSmsOpen}
        onOpenChange={setTxnSmsOpen}
        client={client}
        smsType={txnSmsType}
      />

      {/* v3.15-1124 — Delete Note confirmation dialog */}
      <AlertDialog open={!!noteToDelete} onOpenChange={(o) => { if (!o) setNoteToDelete(null); }}>
        <AlertDialogContent data-testid="delete-note-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              {noteToDelete?.description?.length > 120
                ? `${noteToDelete.description.slice(0, 120)}…`
                : noteToDelete?.description}
              <br />
              <span className="text-xs text-slate-500 mt-2 inline-block">
                This cannot be undone. A deletion record is kept in the system audit log.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-note-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => noteToDelete && handleDeleteNote(noteToDelete.id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-note-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* v3.18-1260 PAYMENT-005 — Shared receipt viewer for timeline payments */}
      <ReceiptViewer
        open={!!timelineReceiptTxId}
        onOpenChange={(v) => { if (!v) setTimelineReceiptTxId(null); }}
        transactionId={timelineReceiptTxId}
      />
    </div>
  );
};

export default ClientDetailPage;
